import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import {
  Card, Button, Upload, message, Space, Tag, Typography, Row, Col,
  Select, Input, Modal, Progress, Popconfirm, Spin, Tooltip, List, Empty, Result,
} from 'antd';
import {
  UploadOutlined, DeleteOutlined, ReloadOutlined, FolderOpenOutlined,
  PlusOutlined, DatabaseOutlined, FileSearchOutlined, CheckCircleOutlined,
  CloudServerOutlined, ThunderboltOutlined, HddOutlined, FileTextOutlined,
  SearchOutlined, InboxOutlined, BarChartOutlined, ClearOutlined,
  BulbOutlined, HistoryOutlined, UserOutlined, EyeOutlined,
  PauseOutlined, CaretRightOutlined, StopOutlined,
} from '@ant-design/icons';
import { kbApi } from '../../services/kbApi';
import api from '../../services/api';
import { useAuthStore } from '../../stores/authStore';
import AiRefinePanel from './AiRefinePanel';
import './KnowledgeBase.css';

const { Text, Title } = Typography;

interface KbDoc { name: string; type: string; chunkCount: number; size: number; createdAt: string; updatedAt: string; categoryId: string | null; }
interface KbCategory { id: string; name: string; description: string; color: string; }
interface KbStats { documentCount: number; totalChunkCount: number; storageSize?: number; totalStorageSize: number; documentsByType?: { type: string; count: number; chunkCount?: number; totalSize?: number; }[]; vectorDimensions: number; lancedbSize?: number; estimatedVectorSize?: number; modelInfo?: { name: string; dimensions: number; loaded: boolean }; }
interface KbStatus { embedding: { loaded: boolean; model: string }; database: { connected: boolean; type: string }; searchIndex: { ready: boolean; count: number }; }

const TYPE_ICONS: Record<string, string> = { '文本': '📄', 'Markdown': '📝', 'PDF': '📕', 'Word': '📘', 'Excel': '📊', '未知': '📎' };
const TYPE_COLORS: Record<string, string> = { '文本': '#1677ff', 'Markdown': '#8b5cf6', 'PDF': '#ff4d4f', 'Word': '#1677ff', 'Excel': '#52c41a', '未知': '#999' };
function formatSize(bytes: number) {
  if (!bytes) return '0 B';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / 1048576).toFixed(1) + ' MB';
}

type WorkKbTab = 'docs' | 'exp' | 'personal-work' | 'team-work' | 'ai-refine';
interface WorkKbEntry {
  id: string;
  ownerUserId?: string;
  targetId?: string;
  targetName?: string;
  source?: string;
  title?: string;
  content?: string;
  chunkIndex?: number;
  createdAt?: string;
  startTime?: number;
  endTime?: number;
}
interface WorkKbStats { totalEntries?: number; targetCount?: number; byTarget?: Record<string, number>; bySource?: Record<string, number>; initialized?: boolean; }

/**
 * work-intel 研判条目的 source 形如 "work_intel:{batchId}|refined@{ISO}":
 * createdAt 是事件真实发生时间,|refined@ 后缀是 AI 研判入库时间。
 * 这里拆出干净的来源标签与研判时间;非研判条目原样返回。
 */
function parseWorkSource(source?: string): { base: string; refinedAt: string | null } {
  const s = source || '';
  const i = s.indexOf('|refined@');
  if (i < 0) return { base: s, refinedAt: null };
  const iso = s.slice(i + '|refined@'.length);
  return { base: s.slice(0, i), refinedAt: isNaN(new Date(iso).getTime()) ? null : iso };
}

// 文档知识库Tab隐藏的分类/文档名前缀（提炼区、情报区只在AI提炼Tab展示）
const KB_EXCLUDE_PREFIXES = ['提炼·', 'AI情报·', '每日技术简报', '设计灵感库', '其他'];

export default function KnowledgeBase() {
  const isAdmin = useAuthStore(s => s.user?.roles?.includes('admin') ?? false);
  const currentUserId = useAuthStore(s => s.user?.id || '');
  const canUpload = useAuthStore(s => {
    const roles = s.user?.roles || [];
    return roles.includes('admin') || roles.includes('kb_upload');
  });
  const [docs, setDocs] = useState<KbDoc[]>([]);
  const [cats, setCats] = useState<KbCategory[]>([]);
  const [stats, setStats] = useState<KbStats | null>(null);
  const [status, setStatus] = useState<KbStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterCat, setFilterCat] = useState<string>('all');
  const [searchText, setSearchText] = useState('');
  const [uploadCat, setUploadCat] = useState<string>('');
  const uploadCatRef = useRef('');
  const setUploadCatBoth = (v: string) => { setUploadCat(v); uploadCatRef.current = v; };
  const [uploadQueue, setUploadQueue] = useState<{ name: string; progress: number; status: string; startedAt: number }[]>([]);
  // 文件夹上传任务面板(只展示当前文件进度 + 总体统计 + 失败列表 + 暂停/中断)
  const [uploadJob, setUploadJob] = useState<{
    total: number; success: number; failed: number;
    // 字节加权:总进度按已处理字节/总字节算,避免大文件被一堆小文件稀释成"看着快好了"
    bytesTotal: number; bytesDone: number;
    current: { name: string; progress: number; status: string; size: number } | null;
    failedList: { name: string; error: string }[];
    paused: boolean; done: boolean; cancelled: boolean;
  } | null>(null);
  const uploadPausedRef = useRef(false);
  const uploadCancelRef = useRef(false);
  const currentAbortRef = useRef<AbortController | null>(null);
  const pauseUpload = () => { uploadPausedRef.current = true; setUploadJob(p => p ? { ...p, paused: true } : p); };
  const resumeUpload = () => { uploadPausedRef.current = false; setUploadJob(p => p ? { ...p, paused: false } : p); };
  const cancelUpload = () => {
    uploadCancelRef.current = true; uploadPausedRef.current = false;
    try { currentAbortRef.current?.abort(); } catch {}
    setUploadJob(p => p ? { ...p, paused: false, cancelled: true } : p);
    message.info('已中断上传，已传完的文件保留');
  };
  const folderInputRef = useRef<HTMLInputElement>(null);
  const [folderUploading, setFolderUploading] = useState(false);
  const [llmStatus, setLlmStatus] = useState('');

  // ── 超大文件上传通道 ──
  interface LargeTaskState {
    taskId: string;
    filename: string;
    fileSize: number;
    status: string;
    phase: string;
    progress: number;
    totalPages?: number;
    parsedPages?: number;
    textLength?: number;
    parseMode?: string;
    message: string;
    error?: string;
  }
  const [largeTask, setLargeTask] = useState<LargeTaskState | null>(null);
  const [largeUploadOpen, setLargeUploadOpen] = useState(false);
  const [largeUploadByteProgress, setLargeUploadByteProgress] = useState(0); // 上传字节进度
  const largeSubRef = useRef<{ close: () => void } | null>(null);
  const largeFileInputRef = useRef<HTMLInputElement>(null);

  // 关闭面板时清理订阅
  const closeLargePanel = () => {
    setLargeUploadOpen(false);
    // 不停订阅——任务在后台跑，下次打开继续看
  };

  // 启动时尝试恢复未完成的大上传任务
  useEffect(() => {
    const storedTaskId = localStorage.getItem('kb_large_task_id');
    if (!storedTaskId) return;
    (async () => {
      try {
        const t = await kbApi.largeUpload.getStatus(storedTaskId);
        if (!t) {
          localStorage.removeItem('kb_large_task_id');
          return;
        }
        // 恢复显示
        setLargeTask(t as any);
        if (t.status === 'queued' || t.status === 'parsing' || t.status === 'embedding' || t.status === 'storing') {
          // 仍在跑，重新订阅
          subscribeLargeTask(storedTaskId);
        } else {
          // 已结束，提示用户并清除
          if (t.status === 'complete') {
            message.success(`上次大文件上传已完成: ${t.filename}`);
          } else if (t.status === 'failed') {
            message.warning(`上次大文件上传失败: ${t.filename}`);
          }
          localStorage.removeItem('kb_large_task_id');
        }
      } catch {
        localStorage.removeItem('kb_large_task_id');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const subscribeLargeTask = (taskId: string) => {
    // 关闭旧订阅
    if (largeSubRef.current) { largeSubRef.current.close(); largeSubRef.current = null; }
    largeSubRef.current = kbApi.largeUpload.subscribe(
      taskId,
      (t) => {
        setLargeTask(t);
      },
      (t) => {
        // 终态
        setLargeTask(t);
        localStorage.removeItem('kb_large_task_id');
        if (t.status === 'complete') {
          message.success(`✅ ${t.filename} 上传完成（${t.result?.chunks || 0}块）`);
          loadAllRef.current(); // 刷新文档列表（用ref避免闭包里的旧页码）
        } else if (t.status === 'failed') {
          message.error(`❌ ${t.filename} 上传失败：${t.error}`);
        } else if (t.status === 'cancelled') {
          message.warning(`已取消：${t.filename}`);
        }
      },
      (err) => {
        console.error('[LARGE-SSE]', err);
        // 不立即报错——可能是网络抖动，30秒后用快照恢复
        setTimeout(async () => {
          try {
            const t = await kbApi.largeUpload.getStatus(taskId);
            setLargeTask(t as any);
            if (t.status === 'parsing' || t.status === 'embedding' || t.status === 'storing' || t.status === 'queued') {
              subscribeLargeTask(taskId); // 重连
            }
          } catch {}
        }, 3000);
      },
    );
  };

  const handleLargeUpload = async (file: File) => {
    if (!file) return;
    const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    const ALLOWED = ['.pdf', '.docx', '.doc', '.txt', '.md', '.xlsx', '.xls', '.csv'];
    if (!ALLOWED.includes(ext)) {
      message.error(`不支持的文件类型: ${ext}`);
      return;
    }
    if (file.size > 100 * 1024 * 1024) {
      message.error(`文件 ${(file.size/1024/1024).toFixed(1)}MB 超过100MB上限`);
      return;
    }
    if (file.size < 1024) {
      message.warning('文件过小，请用普通上传通道');
      return;
    }
    // 已有任务在跑？提醒
    if (largeTask && (largeTask.status === 'queued' || largeTask.status === 'parsing' || largeTask.status === 'embedding' || largeTask.status === 'storing')) {
      message.warning('已有大文件正在上传，请等其完成');
      return;
    }

    setLargeUploadByteProgress(0);
    setLargeUploadOpen(true);
    setLargeTask({
      taskId: '', filename: file.name, fileSize: file.size,
      status: 'uploading', phase: 'upload', progress: 0,
      message: '正在上传文件到服务端...',
    });

    try {
      const task = await kbApi.largeUpload.create(file, uploadCatRef.current || undefined, (pct) => {
        setLargeUploadByteProgress(pct);
      });
      // 保存 taskId 到 localStorage（断网/刷新可恢复）
      localStorage.setItem('kb_large_task_id', task.taskId);
      setLargeTask(task);
      // 启动 SSE
      subscribeLargeTask(task.taskId);
    } catch (e: any) {
      message.error(`上传失败: ${e.message}`);
      setLargeTask({
        taskId: '', filename: file.name, fileSize: file.size,
        status: 'failed', phase: 'upload', progress: 0,
        message: '上传失败', error: e.message,
      });
    }
  };

  const handleLargeCancel = async () => {
    if (!largeTask?.taskId) {
      // 还没创建任务（仍在上传字节阶段），直接关
      setLargeUploadOpen(false);
      setLargeTask(null);
      return;
    }
    try {
      await kbApi.largeUpload.cancel(largeTask.taskId);
      message.info('取消请求已发送');
    } catch (e: any) {
      message.error(`取消失败: ${e.message}`);
    }
  };

  // 确保webkitdirectory在React外设置
  useEffect(() => {
    const el = folderInputRef.current;
    if (el) {
      el.setAttribute('webkitdirectory', '');
      el.setAttribute('directory', '');
    }
  });
  const [catModalOpen, setCatModalOpen] = useState(false);
  const [catName, setCatName] = useState('');
  const [catDesc, setCatDesc] = useState('');
  const [catColor, setCatColor] = useState('#1677ff');
  // 权限管理
  const [permModalOpen, setPermModalOpen] = useState(false);
  const [permUsers, setPermUsers] = useState<any[]>([]);
  const [selectedDocs, setSelectedDocs] = useState<Set<string>>(new Set());
  const [selectMode, setSelectMode] = useState(false);
  const [deletingDoc, setDeletingDoc] = useState<string | null>(null); // 正在删除的文档名
  // ── 经验知识库 ──
  const [kbTab, setKbTab] = useState<WorkKbTab>('docs');
  interface ExpEntry { id: string; question: string; answer: string; summary: string; tags: string[]; userId: string; userName: string; source: string; mode: string; filePath: string | null; createdAt: string; }
  const [expEntries, setExpEntries] = useState<ExpEntry[]>([]);
  const [expTotal, setExpTotal] = useState(0);
  const [expPage, setExpPage] = useState(1);
  const [expSearch, setExpSearch] = useState('');
  const [expLoading, setExpLoading] = useState(false);
  const [expDetail, setExpDetail] = useState<ExpEntry | null>(null);
  const loadExpKb = useCallback(async (page = 1) => {
    setExpLoading(true);
    try {
      const data = await kbApi.experience.list(page, 20, currentUserId);
      setExpEntries(data.items || []);
      setExpTotal(data.total || 0);
    } catch { setExpEntries([]); }
    setExpLoading(false);
  }, [currentUserId]);
  useEffect(() => { if (kbTab === 'exp') loadExpKb(expPage); }, [kbTab, expPage, loadExpKb]);
  const handleExpDelete = async (id: string) => {
    try { await kbApi.experience.delete(id); message.success('已删除'); loadExpKb(expPage); } catch { message.error('删除失败'); }
  };
  const handleExpSearch = async () => {
    if (!expSearch.trim()) { loadExpKb(1); return; }
    setExpLoading(true);
    try {
      const data = await kbApi.experience.search(expSearch, 20, currentUserId);
      setExpEntries(data || []);
      setExpTotal(data?.length || 0);
    } catch { setExpEntries([]); }
    setExpLoading(false);
  };

  const [personalEntries, setPersonalEntries] = useState<WorkKbEntry[]>([]);
  const [personalTotal, setPersonalTotal] = useState(0);
  const [personalPage, setPersonalPage] = useState(1);
  const [personalStats, setPersonalStats] = useState<WorkKbStats | null>(null);
  const [personalLoading, setPersonalLoading] = useState(false);
  const [personalDetail, setPersonalDetail] = useState<WorkKbEntry | null>(null);
  const [teamEntries, setTeamEntries] = useState<WorkKbEntry[]>([]);
  const [teamTotal, setTeamTotal] = useState(0);
  const [teamPage, setTeamPage] = useState(1);
  const [teamStats, setTeamStats] = useState<WorkKbStats | null>(null);
  const [teamLoading, setTeamLoading] = useState(false);
  const [teamDetail, setTeamDetail] = useState<WorkKbEntry | null>(null);

  const loadPersonalWorkKb = useCallback(async (page = 1) => {
    if (!currentUserId) {
      setPersonalEntries([]); setPersonalTotal(0); setPersonalStats(null);
      return;
    }
    setPersonalLoading(true);
    try {
      const [listData, statsData] = await Promise.all([
        kbApi.personalWork.list(page, 20, currentUserId),
        kbApi.personalWork.stats(currentUserId),
      ]);
      setPersonalEntries(listData.items || []);
      setPersonalTotal(listData.total || 0);
      setPersonalStats(statsData || null);
    } catch (e: any) {
      setPersonalEntries([]); setPersonalTotal(0); setPersonalStats(null);
      message.error(e.message || '个人工作知识库加载失败');
    } finally { setPersonalLoading(false); }
  }, [currentUserId]);

  const loadTeamWorkKb = useCallback(async (page = 1) => {
    if (!isAdmin) return;
    setTeamLoading(true);
    try {
      const [listData, statsData] = await Promise.all([
        kbApi.teamWork.list(page, 20),
        kbApi.teamWork.stats(),
      ]);
      setTeamEntries(listData.items || []);
      setTeamTotal(listData.total || 0);
      setTeamStats(statsData || null);
    } catch (e: any) {
      setTeamEntries([]); setTeamTotal(0); setTeamStats(null);
      message.error(e.message || '团队工作知识库加载失败');
    } finally { setTeamLoading(false); }
  }, [isAdmin]);

  useEffect(() => { if (kbTab === 'personal-work') loadPersonalWorkKb(personalPage); }, [kbTab, personalPage, loadPersonalWorkKb]);
  useEffect(() => { if (kbTab === 'team-work' && isAdmin) loadTeamWorkKb(teamPage); }, [kbTab, teamPage, isAdmin, loadTeamWorkKb]);

  const handlePersonalWorkDelete = async (id: string) => {
    try { await kbApi.personalWork.delete(id, currentUserId); message.success('已删除'); loadPersonalWorkKb(personalPage); }
    catch (e: any) { message.error(e.message || '删除失败'); }
  };
  const handleTeamWorkDelete = async (id: string) => {
    if (!isAdmin) return;
    try { await kbApi.teamWork.delete(id); message.success('已删除'); loadTeamWorkKb(teamPage); }
    catch (e: any) { message.error(e.message || '删除失败'); }
  };
  // 上传文件夹历史（最近10次）
  const [uploadHistory, setUploadHistory] = useState<{ name: string; time: string; count: number }[]>(() => {
    try { return JSON.parse(localStorage.getItem('kb_upload_history') || '[]'); } catch { return []; }
  });
  const saveUploadHistory = (folderName: string, fileCount: number) => {
    setUploadHistory(prev => {
      const next = [{ name: folderName, time: new Date().toLocaleString(), count: fileCount }, ...prev.filter(h => h.name !== folderName)].slice(0, 10);
      localStorage.setItem('kb_upload_history', JSON.stringify(next));
      return next;
    });
  };
  // 重复文件弹窗
  const [dupModal, setDupModal] = useState<{file:File;cat:string;onSuccess:any}|null>(null);
  // LLM去重确认弹窗
  const [dedupConfirmModal, setDedupConfirmModal] = useState<{
    files: File[];
    groups: { keep: string; skip: string[] }[];
    skipped: string[];
    allCount: number;
    cat: string;
    llmRan: boolean;
    folderName?: string;
    // 过滤信息（带到弹窗里展示，不能只靠toast）
    totalSelected: number;
    skippedTypes: string[];
    skippedOversize: string[];
    skippedBigTxt: string[];
    duplicateCount: number;
  } | null>(null);
  const [dupAction, setDupAction] = useState<'cover' | 'skip'>('skip');
  // 重复文件选择弹窗
  const [duplicateModal, setDuplicateModal] = useState<{
    files: File[];
    newFiles: File[];
    folderName?: string;
    similarWarn: string[];
    // 过滤信息穿透到LLM弹窗
    skippedTypes: string[];
    skippedOversize: string[];
    skippedBigTxt: string[];
    totalSelected: number;
  } | null>(null);

  // ── 服务端分页状态 ──
  const PAGE_SIZE = 15;
  const [page, setPage] = useState(1);
  const [docsTotal, setDocsTotal] = useState(0);       // 当前筛选条件下的文档总数
  const [docsTotalAll, setDocsTotalAll] = useState(0); // 可见文档总数（不含提炼区）
  const [catCounts, setCatCounts] = useState<Record<string, number>>({});
  const excludeCatIdsRef = useRef<string[]>([]);       // 提炼区分类ID，服务端分页时排除
  const docsReqSeqRef = useRef(0);

  // 按服务端分页拉取当前页文档（提炼区过滤逻辑与原客户端过滤一致，改由服务端执行）
  const fetchDocs = useCallback(async (p: number, cat: string, q: string) => {
    const seq = ++docsReqSeqRef.current;
    const r = await kbApi.listDocumentsPaged({
      page: p, pageSize: PAGE_SIZE,
      q: q || undefined,
      category: cat !== 'all' ? cat : undefined,
      excludeCats: excludeCatIdsRef.current,
      excludePrefixes: KB_EXCLUDE_PREFIXES,
    });
    if (seq !== docsReqSeqRef.current) return; // 丢弃过期响应
    setDocs(r.items || []);
    setDocsTotal(r.total || 0);
    setDocsTotalAll(r.totalAll || 0);
    setCatCounts(r.categoryCounts || {});
    // 删除后当前页可能超界，回退到最后一页
    if (p > 1 && (r.items || []).length === 0 && (r.total || 0) > 0) {
      setPage(Math.max(1, Math.ceil((r.total || 0) / PAGE_SIZE)));
    }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [c, s, st] = await Promise.all([
        kbApi.listCategories(), kbApi.statsDetailed(), kbApi.statsStatus(),
      ]);
      // 过滤掉提炼区和情报区的分类（只在AI提炼Tab中展示），对应文档由服务端分页时排除
      const visibleCats = (c || []).filter((cat: KbCategory) => !KB_EXCLUDE_PREFIXES.some(p => cat.name.startsWith(p)));
      excludeCatIdsRef.current = (c || [])
        .filter((cat: KbCategory) => KB_EXCLUDE_PREFIXES.some(p => cat.name.startsWith(p)))
        .map((cat: KbCategory) => cat.id);
      setCats(visibleCats); setStats(s); setStatus(st);
      await fetchDocs(page, filterCat, searchText);
    } catch (e: any) { setError(e.message || '知识库服务连接失败'); }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchDocs, page, filterCat, searchText]);
  const loadAllRef = useRef(loadAll); loadAllRef.current = loadAll;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAllRef.current(); }, []);

  // 页码/分类/搜索变化时拉取对应页（搜索300ms防抖）
  const firstDocsFetchRef = useRef(true);
  useEffect(() => {
    if (firstDocsFetchRef.current) { firstDocsFetchRef.current = false; return; } // 首次加载由loadAll负责
    const t = setTimeout(() => {
      setLoading(true);
      fetchDocs(page, filterCat, searchText)
        .catch((e: any) => message.error(e.message || '加载文档列表失败'))
        .finally(() => setLoading(false));
    }, searchText ? 300 : 0);
    return () => clearTimeout(t);
  }, [page, filterCat, searchText, fetchDocs]);

  // 页面加载/刷新后恢复上传进度（正在上传时不要恢复，避免与正常上传冲突）
  useEffect(() => {
    if (loading || folderUploading) return;
    let poll: ReturnType<typeof setInterval> | null = null;
    try {
      const stored = localStorage.getItem('kb_active_upload');
      if (!stored) return;
      const task: { taskKey: string; files: string[]; cat: string; done: number; total: number; failed: number; startedAt: number } = JSON.parse(stored);
      if (!task.files || task.files.length === 0) { localStorage.removeItem('kb_active_upload'); return; }
      (async () => {
      // 检查哪些文件已入库(docs现在只是当前页，改为按需拉一次全量老接口)
      let allDocs: KbDoc[] = [];
      try { allDocs = (await kbApi.listDocuments()) || []; } catch { return; }
      const docNames = new Set(allDocs.map(d => d.name));
      const remaining = task.files.filter(f => !docNames.has(f));
      const completed = task.total - remaining.length;
      if (remaining.length === 0) {
        localStorage.removeItem('kb_active_upload');
        message.success(`📁 上传恢复完成：${completed} 个文件已全部入库`, 5);
        return;
      }
      // 检查是否超过5分钟没变化(任务可能已失败)
      if (Date.now() - task.startedAt > 300000 && remaining.length === task.files.length) {
        localStorage.removeItem('kb_active_upload');
        return;
      }
      // 仍有文件未完成，显示恢复状态
      setFolderUploading(true);
      setUploadQueue(remaining.map(f => ({ name: f, progress: 50, status: '🔄 恢复中…', startedAt: Date.now() })));
      message.info(`📁 检测到上传任务：${completed}/${task.total} 已完成，${remaining.length} 个仍在处理中`, 6);
      // 轮询检查
      let checkCount = 0;
      poll = setInterval(async () => {
        try {
          const fresh = await kbApi.listDocuments();
          const freshNames = new Set((fresh || []).map((d: any) => d.name));
          const stillRemaining = remaining.filter(f => !freshNames.has(f));
          const newCompleted = remaining.length - stillRemaining.length;
          if (newCompleted > 0) {
            setUploadQueue(prev => prev.map(q => 
              freshNames.has(q.name) ? { ...q, progress: 100, status: '✅ 完成' } : q
            ));
          }
          if (stillRemaining.length === 0) {
            if (poll) clearInterval(poll);
            setFolderUploading(false);
            localStorage.removeItem('kb_active_upload');
            message.success(`📁 全部上传完成：${task.total} 个`, 5);
            refreshDocs();
            return;
          }
          checkCount++;
          if (checkCount > 20) { // 2分钟后停止轮询
            if (poll) clearInterval(poll);
            setFolderUploading(false);
            localStorage.removeItem('kb_active_upload');
            message.warning(`⚠️ ${stillRemaining.length} 个文件可能上传失败，请手动重试`, 10);
          }
        } catch { checkCount++; }
      }, 6000); // 每6秒检查一次
      })();
      return () => { if (poll) clearInterval(poll); };
    } catch { localStorage.removeItem('kb_active_upload'); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  // 服务端分页：docs 即当前页数据
  const totalPages = Math.max(1, Math.ceil(docsTotal / PAGE_SIZE));
  // 筛选或搜索变化时重置页码
  useEffect(() => { setPage(1); }, [filterCat, searchText]);

  const refreshDocs = async () => { const [, s] = await Promise.all([fetchDocs(page, filterCat, searchText), kbApi.statsDetailed()]); setStats(s); };

  // 上传查重用：临时拉一次全量列表（老接口不带分页参数，与原先常驻的全量docs同源），失败时退回当前页数据
  // 查重用的全量文档名列表。这个请求有 2MB+（6500+篇），每传一个文件拉一次是不可接受的，
  // 而且它挡在上传流程最前面 —— 拉取期间界面毫无反应，用户以为上传没生效。
  // 改为 60 秒缓存；上传成功后把新文件名直接追加进缓存，不必重拉。
  const dedupCacheRef = useRef<{ docs: KbDoc[]; at: number } | null>(null);
  const fetchDocsForDedup = async (): Promise<KbDoc[]> => {
    const c = dedupCacheRef.current;
    if (c && Date.now() - c.at < 60_000) return c.docs;
    try {
      const d = (await kbApi.listDocuments()) || [];
      dedupCacheRef.current = { docs: d, at: Date.now() };
      return d;
    } catch { return docs; }
  };
  const noteUploaded = (name: string) => {
    if (dedupCacheRef.current) dedupCacheRef.current.docs.push({ name } as KbDoc);
  };

  // 把后端 SSE 的进度事件翻译成人话，让用户始终知道后台在干什么
  const stepText = (data: any): string => {
    const map: Record<string, string> = {
      reading: '📖 读取文件…',
      chunking: '✂️ 解析文档内容…',
      ocr: '🔍 扫描件识别中（讯飞OCR）…',
      embedding: '🧠 向量化入库…',
      saving: '💾 写入知识库…',
    };
    if (data?.step === 'embedding' && typeof data.progress === 'number') {
      const pct = Math.max(0, Math.min(100, Math.round((data.progress - 50) / 0.4)));
      return `🧠 向量化入库… ${pct}%`;
    }
    return map[data?.step] || data?.message || '处理中…';
  };

  // 提取文件名基础名（去版本号/日期/序号）
  const getKey = (name: string) => {
    const dot = name.lastIndexOf('.');
    const stem = dot > 0 ? name.slice(0, dot) : name;
    const ext = dot > 0 ? name.slice(dot) : '';
    let base = stem;
    for (let i = 0; i < 3; i++) {
      const prev = base;
      // 只剥离纯数字/版本的括号，不删中文括号
      base = base.replace(/[_\-]*[（(]\d+(?:\.\d+)?[）)]$/, '')   // （1）(1.20)
                 .replace(/[_\-]*[（(]\d{4}-?\d{0,2}-?\d{0,2}[）)]$/, '')  // （2026）(2026-05)
                 .replace(/[_\-]*[vV]\d+\.?\d*$/, '')
                 .replace(/[_\-]*\d{4,}$/, '')
                 .replace(/[_\-]*\d{4}[-_]\d{2}[-_]\d{2}$/, '')
                 .replace(/[_\-]*副本$/, '')
                 .replace(/\.$/, '')
                 .trim();
      if (base === prev) break;
    }
    return `${base}${ext}`;
  };

  const doUpload = async (f: File, cat: string) => {
    setUploadQueue(prev => [...prev, { name: f.name, progress: 0, status: '📤 准备上传', startedAt: Date.now() }]);
    try {
      await kbApi.uploadDocumentStream(f, cat || undefined, (data) => {
        setUploadQueue(prev => prev.map(q => q.name === f.name
          ? { ...q, progress: data.progress, status: data.message || data.step } : q));
      });
      setUploadQueue(prev => prev.map(q => q.name === f.name
        ? { ...q, progress: 100, status: '✅ 完成' } : q));
      refreshDocs();  // 单文件上传后刷新列表
      return true;
    } catch (e: any) {
      setUploadQueue(prev => prev.map(q => q.name === f.name
        ? { ...q, progress: 100, status: '❌ 失败' } : q));
      throw e;
    }
  };

  // 文件夹上传专用：更新已有队列条目（而非新增）
  const doUploadExisting = async (f: File, cat: string) => {
    console.log(`[KB-FOLDER] 开始上传: ${f.name}, size=${f.size}, cat=${cat}`);
    setUploadQueue(prev => prev.map(q => q.name === f.name
      ? { ...q, status: '📤 上传中' } : q));
    try {
      await kbApi.uploadDocumentStream(f, cat || undefined, (data) => {
        setUploadQueue(prev => prev.map(q => q.name === f.name
          ? { ...q, progress: data.progress, status: data.message || data.step } : q));
      });
      console.log(`[KB-FOLDER] 上传成功: ${f.name}`);
      setUploadQueue(prev => prev.map(q => q.name === f.name
        ? { ...q, progress: 100, status: '✅ 完成' } : q));
      // 不再每个文件刷新一次列表，整批结束后由 executeFolderUpload 统一 refreshDocs()
      return true;
    } catch (e: any) {
      console.error(`[KB-FOLDER] 上传失败: ${f.name}, error=${e.message}`);
      setUploadQueue(prev => prev.map(q => q.name === f.name
        ? { ...q, progress: 100, status: '❌ 失败' } : q));
      throw e;
    }
  };

  const handleUpload = async (options: any) => {
    const { file, onSuccess, onError } = options;
    const f = file as File;
    const cat = uploadCatRef.current;
    console.log(`[KB-DIAG] ▶ handleUpload被调用: name="${f.name}", size=${f.size}, type="${f.type}", cat="${cat}"`);

    // 检查文件类型
    const ext = '.' + f.name.split('.').pop()?.toLowerCase();
    const ALLOWED_EXTS = new Set(['.doc', '.docx', '.pdf', '.xls', '.xlsx', '.txt']);
    console.log(`[KB-DIAG] ext="${ext}", allowed=${ALLOWED_EXTS.has(ext)}`);
    if (!ALLOWED_EXTS.has(ext)) {
      console.warn(`[KB-DIAG] ✗ 扩展名拦截: ext="${ext}"`);
      message.error(`不支持的文件类型 "${ext}"，仅允许 doc/docx/pdf/xls/xlsx/txt`);
      onError?.(new Error(`不支持的文件类型 "${ext}"，仅允许 doc/docx/pdf/xls/xlsx/txt`));
      return;
    }

    // 检查文件大小：PDF限15MB，TXT/表格限5MB，Word不限
    const PDF_MAX_SIZE = 15 * 1024 * 1024;
    const OTHER_MAX_SIZE = 5 * 1024 * 1024;
    const OVER_SIZE_EXTS = new Set(['.txt', '.xls', '.xlsx']);
    if (ext === '.pdf' && f.size > PDF_MAX_SIZE) {
      const sizeMB = (f.size / 1024 / 1024).toFixed(1);
      console.warn(`[KB-DIAG] ✗ PDF超大拦截: ${sizeMB}MB`);
      message.warning(`已跳过 ${f.name}（${sizeMB}MB，PDF超过15MB自动忽略）`);
      onError?.(new Error(`文件过大（${sizeMB}MB），PDF限15MB`));
      return;
    }
    if (OVER_SIZE_EXTS.has(ext) && f.size > OTHER_MAX_SIZE) {
      const sizeMB = (f.size / 1024 / 1024).toFixed(1);
      console.warn(`[KB-DIAG] ✗ TXT/表格超大拦截: ${sizeMB}MB`);
      message.warning(`已跳过 ${f.name}（${sizeMB}MB，TXT/表格超过5MB自动忽略）`);
      onError?.(new Error(`文件过大（${sizeMB}MB），TXT/表格限5MB`));
      return;
    }

    const setQ = (patch: Partial<{ progress: number; status: string }>) =>
      setUploadQueue(prev => prev.map(q => (q.name === f.name ? { ...q, ...patch } : q)));

    const doUploadFlow = async () => {
      setQ({ progress: 5, status: '📤 上传文件中…' });
      try {
        await kbApi.uploadDocumentStream(f, cat || undefined, (data) => {
          setQ({ progress: data.progress, status: stepText(data) });
        });
        setQ({ progress: 100, status: '✅ 已入库' });
        noteUploaded(f.name);
        onSuccess?.({}, f); message.success(`上传成功: ${f.name}`);
        await refreshDocs();
      } catch (e: any) {
        setQ({ progress: 100, status: `❌ 失败：${e.message}` });
        onError?.(new Error(e.message)); message.error(e.message);
      }
      setTimeout(() => setUploadQueue(prev => prev.filter(q => q.name !== f.name)), 6000);
    };

    // 点击之后必须立刻有反应 —— 先把这个文件放进上传面板，再去做耗时的查重。
    // 原先是先 await 查重(2MB全量列表)、拿到结果才创建队列项，界面在此期间毫无变化。
    setUploadQueue(prev => [
      ...prev.filter(q => q.name !== f.name),
      { name: f.name, progress: 0, status: '🔍 检查是否已存在…', startedAt: Date.now() },
    ]);

    const dedupDocs = await fetchDocsForDedup();
    const dupName = dedupDocs.find(d => d.name === f.name);
    if (dupName) {
      console.warn(`[KB-DIAG] ✗ 同名拦截 → 弹dupModal: "${f.name}"`);
      setQ({ status: '⏸ 已存在同名文件，等待你确认…' });
      setDupModal({ file: f, cat, onSuccess });
      return;
    }

    // 智能检测：是否有同名但不同版本的文档
    const upKey = getKey(f.name);
    const similar = dedupDocs.filter(d => d.name !== f.name && getKey(d.name) === upKey);
    if (similar.length > 0) {
      const simNames = similar.map(d => d.name).slice(0, 5).join('、');
      console.log(`[KB-DIAG] 相似文件提示: ${similar.length}个相似 → ${simNames}`);
      message.warning(`检测到 ${similar.length} 个同类文件: ${simNames}${similar.length>5?'...':''}，可先清理旧版再上传`);
    }

    console.log(`[KB-DIAG] ✓ 通过所有拦截，调用doUploadFlow()`);
    doUploadFlow();
  };

  // 智能去重：提取基础名 + 版本号，同基础名只保留最新版本
  const smartDedup = (files: File[]) => {
    const getVersion = (name: string) => {
      const stem = name.replace(/\.[^.]+$/, '');
      const dateMatch = stem.match(/(\d{4,})$/);
      const dateMatch2 = stem.match(/(\d{4}[-_]\d{2}[-_]\d{2})$/);
      const vMatch = stem.match(/[vV](\d+)(?:\.(\d+))?$/);
      const numMatch = stem.match(/[（(](\d+(?:\.\d+)?)[）)]$/);
      const underMatch = stem.match(/_(\d+)$/);
      const dashMatch = stem.match(/-(\d+)$/);
      if (dateMatch) return parseInt(dateMatch[1]);
      if (dateMatch2) return parseInt(dateMatch2[1].replace(/[-_]/g, ''));
      if (vMatch) return parseInt(vMatch[1]) * 10000 + parseInt(vMatch[2] || '0') * 100;
      if (numMatch) return Math.round(parseFloat(numMatch[1]) * 100);
      if (underMatch) return parseInt(underMatch[1]);
      if (dashMatch) return parseInt(dashMatch[1]);
      return 0;
    };

    const groups = new Map<string, { file: File; version: number; name: string }[]>();
    for (const f of files) {
      if (/[_\-\s（(]*副本[)）]?/i.test(f.name)) continue;
      const key = getKey(f.name);
      const ver = getVersion(f.name);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push({ file: f, version: ver, name: f.name });
    }

    const winners: File[] = [];
    for (const [, items] of groups) {
      if (items.length === 1) { winners.push(items[0].file); }
      else {
        items.sort((a, b) => b.version - a.version);
        winners.push(items[0].file);
      }
    }
    return winners;
  };

  // LLM去重 + 弹窗确认（提取为独立函数，供重复文件弹窗回调）
  const proceedToLlmDedup = async (
    deduped: File[], 
    totalCount: number, 
    folderName?: string,
    skippedTypes: string[] = [],
    skippedOversize: string[] = [],
    totalSelected: number = totalCount,
    duplicateCount: number = 0,
    skippedBigTxt: string[] = [],
  ) => {
    console.log(`[KB-DEDUP] proceedToLlmDedup开始: deduped=${deduped.length}, totalCount=${totalCount}, folderName=${folderName}`);
    let files = deduped;
    let llmGroups: { keep: string; skip: string[] }[] = [];
    let llmSkipped: string[] = [];
    let llmRan = false;

    if (deduped.length > 1000) {
      message.error(`选中 ${deduped.length} 个文件，超过自动去重上限 1000，请分批上传`);
      setLlmStatus(`⚠️ 文件超过 1000 个，未做 AI 去重，请分批上传`);
    } else if (deduped.length > 1) {
      setLlmStatus(`🤖 AI正在分析 ${deduped.length} 个文件...`);
      try {
        const names = deduped.map(f => f.name);
        const fileMeta = deduped.map(f => ({
          name: f.name,
          // 真实修改时间精确到分秒(判"最新版"更准;文件名后缀不一定是日期)
          lastModified: new Date(f.lastModified).toISOString().slice(0, 19).replace('T', ' '),
          size: f.size,
        }));
        console.log(`[KB-DEDUP] 调用LLM去重API: names=${names.length}, timeout=120s`);
        const res = await api.post('/dev-workspace/sessions/dedup-names', { filenames: names, fileMeta }, { timeout: 200000 });
        console.log(`[KB-DEDUP] LLM去重API返回: data=${JSON.stringify(res.data)?.slice(0, 500)}`);
        const llmKeep: string[] = res.data?.filenames || names;
        llmSkipped = res.data?.skipped || [];
        llmGroups = res.data?.groups || [];
        llmRan = true;

        if (llmSkipped.length > 0) {
          files = deduped.filter(f => llmKeep.includes(f.name));
          console.log(`[KB-DEDUP] LLM跳过${llmSkipped.length}个: ${llmSkipped.join(',')}`);
          setLlmStatus(`🤖 AI检测到 ${llmSkipped.length} 个旧版/重复文件，请确认后上传`);
        } else {
          console.log(`[KB-DEDUP] LLM无跳过, 全部保留`);
          setLlmStatus(`🤖 AI分析完成，${deduped.length} 个文件，请确认后上传`);
        }
      } catch (e: any) {
        console.error(`[KB-DEDUP] LLM去重API失败: ${e.message}`);
        setLlmStatus('⚠️ AI服务暂不可用，请确认后上传');
      }
    } else if (deduped.length === 1) {
      setLlmStatus('仅1个文件，请确认后上传');
    }

    setDedupConfirmModal({
      files: llmSkipped.length > 0 ? files : deduped,
      groups: llmGroups,
      skipped: llmSkipped,
      allCount: totalCount || deduped.length,
      cat: uploadCatRef.current,
      llmRan,
      folderName,
      totalSelected,
      skippedTypes,
      skippedOversize,
      skippedBigTxt,
      duplicateCount,
    });
  };

  // 文件夹上传：最多2个并发
  const handleFolderUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const allFiles = Array.from(e.target.files || []);
    console.log(`[KB-HANDLE] handleFolderUpload触发: allFiles=${allFiles.length}`);
    if (allFiles.length === 0) return;

    // 提取文件夹名
    const folderName = allFiles[0]?.webkitRelativePath?.split('/')[0] || '未知文件夹';

    // 过滤不支持的文件类型：只允许 doc/docx/pdf/xls/xlsx/txt
    const ALLOWED_EXTS = new Set(['.doc', '.docx', '.pdf', '.xls', '.xlsx', '.txt']);
    const PDF_MAX_SIZE = 15 * 1024 * 1024;  // PDF限15MB
    const OTHER_MAX_SIZE = 10 * 1024 * 1024; // 表格限10MB
    const TXT_MAX_SIZE = 2 * 1024 * 1024;   // TXT限2MB:大txt多为聊天记录导出,会切成上万chunk拖垮向量化/拉满负载,直接过滤
    const skippedTypes: string[] = [];
    const skippedOversize: string[] = [];
    const skippedBigTxt: string[] = [];
    const validFiles = allFiles.filter(f => {
      const ext = '.' + f.name.split('.').pop()?.toLowerCase();
      if (!ALLOWED_EXTS.has(ext)) { skippedTypes.push(f.name); return false; }
      if (ext === '.txt' && f.size > TXT_MAX_SIZE) {
        skippedBigTxt.push(f.name); return false;
      }
      if (ext === '.pdf' && f.size > PDF_MAX_SIZE) {
        skippedOversize.push(f.name); return false;
      }
      if (ext !== '.pdf' && ext !== '.doc' && ext !== '.docx' && ext !== '.txt' && f.size > OTHER_MAX_SIZE) {
        skippedOversize.push(f.name); return false;
      }
      return true;
    });

    // 汇总提示：数字必须对上
    if (skippedTypes.length > 0 || skippedOversize.length > 0 || skippedBigTxt.length > 0) {
      const parts: string[] = [];
      parts.push(`📁 文件夹共 ${allFiles.length} 个文件`);
      if (skippedTypes.length > 0) {
        parts.push(`❌ 类型不支持：${skippedTypes.length} 个（${skippedTypes.slice(0,3).join('、')}${skippedTypes.length>3?`等` : ''}）`);
      }
      if (skippedBigTxt.length > 0) {
        parts.push(`📄 txt超过2MB已跳过：${skippedBigTxt.length} 个（${skippedBigTxt.slice(0,3).join('、')}${skippedBigTxt.length>3?`等` : ''}）`);
      }
      if (skippedOversize.length > 0) {
        parts.push(`📦 超过大小限制：${skippedOversize.length} 个（${skippedOversize.slice(0,3).join('、')}${skippedOversize.length>3?`等` : ''}）`);
      }
      parts.push(`✅ 将上传：${validFiles.length} 个`);
      message.info(parts.join(' | '), 8);
    }
    if (validFiles.length === 0) { message.info('没有可上传的文件（仅支持 doc/docx/pdf/xls/xlsx/txt）'); return; }

    // 文件夹上传不做智能去重，用户选的就是要上传的

    // ── 检查已有文档（在LLM之前做），弹窗让用户选择 ──
    // 查重数据源：上传时临时拉一次全量列表（查重逻辑本身不变）
    const dedupDocs = await fetchDocsForDedup();
    const existingNames = new Set(dedupDocs.map(d => d.name));
    const existingKeys = new Map<string, string[]>();
    for (const doc of dedupDocs) {
      const k = getKey(doc.name);
      if (!existingKeys.has(k)) existingKeys.set(k, []);
      existingKeys.get(k)!.push(doc.name);
    }
    const dupFiles: File[] = [];
    const similarWarn: string[] = [];
    for (const f of validFiles) {
      if (existingNames.has(f.name)) {
        dupFiles.push(f);
      } else {
        const k = getKey(f.name);
        const sim = existingKeys.get(k);
        if (sim && sim.length > 0) similarWarn.push(`${f.name} → 已有: ${sim[0]}`);
      }
    }
    if (similarWarn.length > 0) {
      message.info(`🔍 ${similarWarn.length} 个文件与已有文档同类(不同版本): ${similarWarn.slice(0,3).join('；')}${similarWarn.length>3?`等${similarWarn.length}个`:''}`, 5);
    }

    // 如果有重复文件，暂停并弹窗让用户选择
    if (dupFiles.length > 0) {
      setDuplicateModal({
        files: dupFiles,
        newFiles: validFiles.filter(f => !existingNames.has(f.name)),
        folderName,
        similarWarn,
        skippedTypes,
        skippedOversize,
        skippedBigTxt,
        totalSelected: allFiles.length,
      });
      return; // 等用户在弹窗中做决定
    }

    // 没有重复，直接进入LLM去重（携带过滤信息）
    proceedToLlmDedup(validFiles, validFiles.length, folderName, skippedTypes, skippedOversize, allFiles.length, dupFiles.length, skippedBigTxt);
  };

  // 执行文件夹上传（LLM确认后的上传逻辑）
  const executeFolderUpload = async (files: File[], allCount: number, swSkipped: number) => {
    console.log(`[KB-EXEC] executeFolderUpload开始: files=${files.length}, allCount=${allCount}, swSkipped=${swSkipped}`);
    console.log(`[KB-EXEC] 文件列表: ${files.map(f => `${f.name}(${f.size})`).join(', ')}`);

    // 检查与已有文档的基础名重复（不同版本号但核心相同的文档）
    // 查重数据源：上传时临时拉一次全量列表（查重逻辑本身不变）
    const dedupDocs = await fetchDocsForDedup();
    const existingKeys = new Map<string, string[]>();
    for (const doc of dedupDocs) {
      const k = getKey(doc.name);
      if (!existingKeys.has(k)) existingKeys.set(k, []);
      existingKeys.get(k)!.push(doc.name);
    }
    const conflictNames: string[] = [];
    for (const f of files) {
      const k = getKey(f.name);
      const existing = existingKeys.get(k);
      if (existing && !existing.includes(f.name)) {
        conflictNames.push(`${f.name} → 已有: ${existing[0]}`);
      }
    }
    if (conflictNames.length > 0) {
      const detail = conflictNames.slice(0,3).join('；');
      setLlmStatus(prev => `${prev} ⚠️ ${detail}${conflictNames.length>3?`等${conflictNames.length}个` : ''} 与已有文档同类`);
    }

    // 检查已有文档（精确同名）
    const existingNames = new Set(dedupDocs.map(d => d.name));
    const dupFiles = files.filter(f => existingNames.has(f.name));
    if (dupFiles.length > 0) {
      const dupNames = dupFiles.map(f => f.name).slice(0, 5).join('、');
      console.log(`[KB-EXEC] 跳过已存在文件: ${dupNames}`);
      message.warning(`⚠️ 跳过 ${dupFiles.length} 个已存在的文件: ${dupNames}${dupFiles.length>5?'...':''}；将上传 ${files.length - dupFiles.length} 个新文件`, 8);
      files = files.filter(f => !existingNames.has(f.name));
    }

    if (files.length === 0) { message.info('没有需要上传的文件'); return; }

    setFolderUploading(true);
    const cat = uploadCatRef.current;
    console.log(`[KB-EXEC] 开始上传, cat=${cat}, 实际上传${files.length}个文件`);
    // 预填充：所有文件初始状态为「待处理」
    // 存上传任务到localStorage(跨页面刷新恢复)
    const taskKey = `kb_upload_${Date.now()}`;
    try {
      localStorage.setItem('kb_active_upload', JSON.stringify({
        taskKey, files: files.map(f => f.name), cat, done: 0, total: files.length, startedAt: Date.now()
      }));
    } catch {}
    // 初始化任务面板 + 重置暂停/中断标志
    const bytesTotal = files.reduce((s, f) => s + (f.size || 0), 0);
    setUploadJob({ total: files.length, success: 0, failed: 0, bytesTotal, bytesDone: 0, current: null, failedList: [], paused: false, done: false, cancelled: false });
    uploadPausedRef.current = false;
    uploadCancelRef.current = false;
    let success = 0;
    let failed = 0;
    let bytesDone = 0;
    const failedList: { name: string; error: string }[] = [];
    // 串行:一次只传一个,面板只展示当前文件进度
    for (const f of files) {
      if (uploadCancelRef.current) break;
      // 暂停:卡在这里等"继续"或"中断"
      while (uploadPausedRef.current && !uploadCancelRef.current) {
        await new Promise(r => setTimeout(r, 200));
      }
      if (uploadCancelRef.current) break;
      setUploadJob(prev => prev ? { ...prev, current: { name: f.name, progress: 0, status: '⏳ 开始上传', size: f.size || 0 } } : prev);
      const ctrl = new AbortController();
      currentAbortRef.current = ctrl;
      try {
        await kbApi.uploadDocumentStream(f, cat || undefined, (data: any) => {
          const p = typeof data?.progress === 'number' ? data.progress : 0;
          const st = data?.message || data?.step || '';
          setUploadJob(prev => (prev && prev.current) ? { ...prev, current: { ...prev.current, progress: p, status: st || prev.current.status } } : prev);
        }, ctrl.signal);
        success++;
        bytesDone += (f.size || 0);
        setUploadJob(prev => prev ? { ...prev, success, bytesDone } : prev);
        // 每传完一个就实时刷新统计(文档总数/分块数)+列表;不 await,避免拖慢下一个文件
        // 列表已分页、stats是独立轻接口,逐个刷新不会卡
        refreshDocs().catch(() => {});
      } catch (e: any) {
        // 主动中断(用户点中断→abort)或服务端回 cancelled:不计失败,直接停
        if (uploadCancelRef.current || e?.name === 'AbortError' || e?.message === '__CANCELLED__') break;
        failed++;
        bytesDone += (f.size || 0); // 失败的文件也算"已处理完",让总进度继续推进
        failedList.push({ name: f.name, error: (e?.message || '上传失败').slice(0, 120) });
        setUploadJob(prev => prev ? { ...prev, failed, bytesDone, failedList: [...failedList] } : prev);
      } finally {
        currentAbortRef.current = null;
      }
      try {
        localStorage.setItem('kb_active_upload', JSON.stringify({
          taskKey, files: files.slice(success + failed).map(x => x.name), cat, done: success + failed, total: files.length, failed, startedAt: Date.now()
        }));
      } catch {}
    }
    const cancelled = uploadCancelRef.current;
    setUploadJob(prev => prev ? { ...prev, current: null, done: true } : prev);
    await refreshDocs();
    try { localStorage.removeItem('kb_active_upload'); } catch {}
    console.log(`[KB-EXEC] 结束: success=${success}, failed=${failed}, total=${files.length}, cancelled=${cancelled}`);
    if (cancelled) {
      message.info(`已中断：✅ ${success} 成功，❌ ${failed} 失败，其余未上传`, 6);
    } else if (failed > 0) {
      message.warning(`📁 上传完成：✅ ${success} 个成功，❌ ${failed} 个失败`, 8);
    } else {
      message.success(`📁 上传完成：✅ ${success} 个全部成功`, 5);
    }
    setFolderUploading(false);
    if (folderInputRef.current) folderInputRef.current.value = '';
  };

  const handleDelete = async (filename: string) => {
    setDeletingDoc(filename);
    try { await kbApi.deleteDocument(filename); message.success('已删除'); await refreshDocs(); } catch (e: any) { message.error(e.message || '删除失败'); }
    finally { setDeletingDoc(null); }
  };
  const handleBatchDelete = async () => {
    for (const name of selectedDocs) { try { await kbApi.deleteDocument(name); } catch {} }
    message.success(`已删除 ${selectedDocs.size} 个文档`);
    setSelectedDocs(new Set()); setSelectMode(false); await refreshDocs();
  };
  const handleClearAll = async () => {
    // docs现在只是当前页：先拉全量，再按与原来完全相同的可见范围过滤（严禁误删提炼区/情报区文档）
    const all = await fetchDocsForDedup();
    const excludeIds = new Set(excludeCatIdsRef.current);
    const visibleAll = all.filter(d => !excludeIds.has(d.categoryId || '') && !KB_EXCLUDE_PREFIXES.some(p => d.name.startsWith(p)));
    for (const doc of visibleAll) { try { await kbApi.deleteDocument(doc.name); } catch {} }
    message.success('已清空所有文档');
    setSelectedDocs(new Set()); await refreshDocs();
  };

  const handleCreateCat = async () => {
    const name = catName.trim(); if (!name) { message.warning('请输入分类名称'); return; }
    try { await kbApi.createCategory(name, catDesc || undefined, catColor); message.success(`已创建: ${name}`);
      setCatModalOpen(false); setCatName(''); setCatDesc(''); setCatColor('#1677ff');
      const c = await kbApi.listCategories(); setCats(c || []);
    } catch (e: any) { message.error(e.message); }
  };
  const handleDeleteCat = async (id: string) => {
    try { await kbApi.deleteCategory(id); message.success('已删除');
      const c = await kbApi.listCategories(); setCats(c || []); if (filterCat === id) setFilterCat('all');
    } catch (e: any) { message.error(e.message); }
  };
  const handleSetCat = async (filename: string, categoryId: string) => {
    try { await kbApi.setDocCategory(filename, categoryId); await refreshDocs(); } catch (e: any) { message.error(e.message); }
  };

  // ── 权限管理（admin） ──
  const loadUsers = async () => {
    try { const { data } = await api.get('/users?pageSize=200'); setPermUsers(data.items || []); } catch {}
  };
  const toggleKbPerm = async (userId: string, hasPerm: boolean) => {
    try {
      await api.put(`/users/${userId}/roles`, { roleCode: 'kb_upload', action: hasPerm ? 'remove' : 'add' });
      message.success(hasPerm ? '已移除权限' : '已授予权限');
      loadUsers();
    } catch (e: any) { message.error(e.response?.data?.message || '操作失败'); }
  };

  const toggleSelect = (name: string) => {
    setSelectedDocs(prev => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  };

  if (error && !loading) {
    return (
      <div className="kb-wrap">
        <div className="kb-glass-card" style={{ textAlign: 'center', padding: 80 }}>
          <CloudServerOutlined style={{ fontSize: 48, color: '#ff4d4f', opacity: .6 }} />
          <Title level={4} style={{ marginTop: 16, color: '#ff4d4f' }}>知识库服务连接失败</Title>
          <Text type="secondary">{error}</Text><br />
          <Button type="primary" icon={<ReloadOutlined />} onClick={loadAll} style={{ marginTop: 16 }}>重试</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="kb-wrap">
      {/* ── Document Management Card ── */}
      <Card className="kb-glass-card" bodyStyle={{ padding: 24 }}
        title={
          <Space>
            <FolderOpenOutlined style={{ color: '#1677ff' }} />
            <span>知识库管理</span>
          </Space>
        }
        extra={
          <Space size={8}>
            {status && (
              <Space size={12} style={{ marginRight: 8 }}>
                <Tag color={status.embedding?.loaded ? 'green' : 'orange'} style={{ fontSize: 11 }}>
                  <ThunderboltOutlined /> {status.embedding?.model || 'Model'}
                </Tag>
                <Tag color={status.database?.connected ? 'green' : 'red'} style={{ fontSize: 11 }}>
                  <HddOutlined /> {status.database?.type || 'DB'}
                </Tag>
                <Tag color={status.searchIndex?.ready ? 'green' : 'orange'} style={{ fontSize: 11 }}>
                  <FileSearchOutlined /> 索引 {status.searchIndex?.count ?? 0}
                </Tag>
              </Space>
            )}
            <Button size="small" icon={<ReloadOutlined />} onClick={loadAll} loading={loading}>刷新</Button>
          </Space>
        }
      >
        {/* ── Tab切换 ── */}
        <div className="kb-tab-bar">
          <Button className={`kb-tab-btn ${kbTab === 'docs' ? 'active' : ''}`} type={kbTab === 'docs' ? 'primary' : 'default'}
            onClick={() => setKbTab('docs')}>
            <FolderOpenOutlined /> <span>文档知识库</span>
          </Button>
          <Button className={`kb-tab-btn ${kbTab === 'exp' ? 'active' : ''}`} type={kbTab === 'exp' ? 'primary' : 'default'}
            icon={<BulbOutlined />}
            onClick={() => setKbTab('exp')}>经验知识库</Button>
          <Button className={`kb-tab-btn ${kbTab === 'personal-work' ? 'active' : ''}`} type={kbTab === 'personal-work' ? 'primary' : 'default'}
            icon={<UserOutlined />}
            onClick={() => setKbTab('personal-work')}>个人工作知识库</Button>
          {isAdmin && (
            <Button className={`kb-tab-btn ${kbTab === 'team-work' ? 'active' : ''}`} type={kbTab === 'team-work' ? 'primary' : 'default'}
              icon={<DatabaseOutlined />}
              onClick={() => setKbTab('team-work')}>团队工作知识库</Button>
          )}
          <Button className={`kb-tab-btn ${kbTab === 'ai-refine' ? 'active' : ''}`} type={kbTab === 'ai-refine' ? 'primary' : 'default'}
            icon={<ThunderboltOutlined />}
            onClick={() => setKbTab('ai-refine')}>AI提炼</Button>
        </div>

        {kbTab === 'docs' && (<div key="docs">
        {/* ── Stats ── */}
        {stats && (
          <Row gutter={16} style={{ marginBottom: 20 }}>
            <Col xs={12} sm={6}>
              <div className="kb-stat">
                <div className="kb-stat-val">{stats.documentCount}</div>
                <div className="kb-stat-lbl"><DatabaseOutlined /> 文档总数</div>
              </div>
            </Col>
            <Col xs={12} sm={6}>
              <div className="kb-stat">
                <div className="kb-stat-val">{stats.totalChunkCount}</div>
                <div className="kb-stat-lbl"><FileTextOutlined /> 分块数量</div>
              </div>
            </Col>
            <Col xs={12} sm={6}>
              <div className="kb-stat">
                <div className="kb-stat-val">{stats.vectorDimensions || 512}</div>
                <div className="kb-stat-lbl"><BarChartOutlined /> 向量维度</div>
              </div>
            </Col>
            <Col xs={12} sm={6}>
              <div className="kb-stat">
                <div className="kb-stat-val">{formatSize(stats.totalStorageSize)}</div>
                <div className="kb-stat-lbl"><CloudServerOutlined /> 存储大小</div>
              </div>
            </Col>
          </Row>
        )}

        {/* ── Toolbar (admin only) ── */}
        {!isAdmin ? (
          <div style={{ textAlign: 'center', padding: '20px 0', color: '#999' }}>
            <InboxOutlined style={{ fontSize: 36 }} />
            <p>文档列表和上传功能仅限管理员操作</p>
            <p style={{ fontSize: 12 }}>您可以在 AI 全流程的设计模式中使用知识库进行检索</p>
          </div>
        ) : (
          <><div className="kb-cats">
          <span className="kb-cats-label">分类:</span>
          <Tag className={`kb-cat-chip ${filterCat === 'all' ? 'active' : ''}`}
            color={filterCat === 'all' ? '#1677ff' : undefined}
            onClick={() => setFilterCat('all')}>
            全部 ({docsTotalAll})
          </Tag>
          {cats.map(c => (
            <span key={c.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 0 }}>
              <Tag className={`kb-cat-chip ${filterCat === c.id ? 'active' : ''}`}
                color={filterCat === c.id ? c.color : undefined}
                style={filterCat === c.id ? { borderColor: c.color, color: c.color, background: `${c.color}15` } : {}}
                onClick={() => { const newCat = filterCat === c.id ? 'all' : c.id; setFilterCat(newCat); setUploadCatBoth(newCat === 'all' ? '' : newCat); }}>
                {c.name} ({catCounts[c.id] || 0})
              </Tag>
              {c.id !== 'default' && (
                <Popconfirm overlayClassName="kb-pop-light" title="删除此分类？" onConfirm={() => handleDeleteCat(c.id)}>
                  <Button type="text" danger size="small" icon={<DeleteOutlined style={{ fontSize: 11 }} />}
                    style={{ padding: '0 4px', height: 22 }} />
                </Popconfirm>
              )}
            </span>
          ))}
          <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={() => setCatModalOpen(true)} style={{ height: 24 }}>新建</Button>
        </div>

        {/* ── Toolbar ── */}
        <div className="kb-toolbar">
          <Input prefix={<SearchOutlined />} placeholder="搜索文档..." size="small" style={{ width: 240 }}
            allowClear value={searchText} onChange={e => setSearchText(e.target.value)} />
          <Select value={uploadCat || undefined} style={{ width: 130 }} size="small" placeholder="上传到分类"
            onChange={(v) => setUploadCatBoth(v || '')} options={cats.map(c => ({ label: c.name, value: c.id }))} />
          {canUpload && <Upload customRequest={handleUpload} showUploadList={false} accept=".txt,.md,.pdf,.docx,.doc,.xlsx,.xls">
            <Button type="primary" size="small" icon={<UploadOutlined />}>上传文档</Button>
          </Upload>}
          {canUpload && <Button size="small" icon={<FolderOpenOutlined />} loading={folderUploading}
            onClick={() => folderInputRef.current?.click()}>上传文件夹</Button>}
          <input type="file" ref={folderInputRef} multiple
            onChange={handleFolderUpload} accept=".txt,.md,.pdf,.docx,.doc,.xlsx,.xls" style={{ position:'absolute', left:-9999, top:-9999 }} />
          {canUpload && <>
            <Button size="small" icon={<CloudServerOutlined />}
              onClick={() => largeFileInputRef.current?.click()}
              style={{ background: '#fff7e6', borderColor: '#ffd591', color: '#d46b08' }}>
              📦 超大文件 (≤100MB)
            </Button>
            <input type="file" ref={largeFileInputRef}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleLargeUpload(f);
                if (e.target) e.target.value = ''; // 允许重新选同一个文件
              }}
              accept=".pdf,.docx,.doc,.txt,.md,.xlsx,.xls,.csv"
              style={{ position:'absolute', left:-9999, top:-9999 }} />
            {largeTask && (largeTask.status === 'queued' || largeTask.status === 'parsing' || largeTask.status === 'embedding' || largeTask.status === 'storing' || largeTask.status === 'uploading') && (
              <Button size="small" type="link" onClick={() => setLargeUploadOpen(true)}
                style={{ color: '#d46b08', padding: '0 8px' }}>
                查看进度 ({largeTask.progress}%)
              </Button>
            )}
          </>}
          {isAdmin && <Button size="small" onClick={() => { loadUsers(); setPermModalOpen(true); }}>🔐 权限管理</Button>}
          {llmStatus && (
            <span title={llmStatus} style={{
              background: 'linear-gradient(135deg, #e6f7ff, #bae7ff)',
              border: '1px solid #91d5ff',
              borderRadius: 6,
              padding: '4px 14px',
              fontSize: 13,
              fontWeight: 600,
              color: '#0050b3',
              marginLeft: 8,
              display: 'inline-block',
              verticalAlign: 'middle',
              maxWidth: 'min(100%, 520px)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {llmStatus}
            </span>
          )}
          <div style={{ flex: 1 }} />
          {selectMode ? (
            <Space size={8}>
              <Button size="small" onClick={() => { setSelectedDocs(new Set(docs.map(d => d.name))); }}>
                全选本页 ({docs.length})
              </Button>
              <Button size="small" danger onClick={handleBatchDelete} disabled={selectedDocs.size === 0}>
                <DeleteOutlined /> 删除 ({selectedDocs.size})
              </Button>
              <Button size="small" onClick={() => { setSelectMode(false); setSelectedDocs(new Set()); }}>取消</Button>
            </Space>
          ) : (
            <Space size={8}>
              <Button size="small" onClick={() => setSelectMode(true)} disabled={docs.length === 0}>
                <CheckCircleOutlined /> 批量选择
              </Button>
              <Button size="small" danger onClick={handleClearAll} disabled={docsTotalAll === 0} icon={<ClearOutlined />}>
                清空
              </Button>
            </Space>
          )}
        </div>

        {/* ── Upload Queue ── */}
        {/* 单文件上传进度面板。原先 uploadQueue 只更新 state、JSX 里从来没渲染过，
            加上 Upload 组件又是 showUploadList={false}，导致点了上传之后界面上什么都不显示。
            文件夹上传另有 uploadJob 面板，两者不同时出现。 */}
        {!uploadJob && uploadQueue.length > 0 && (
          <div className="kb-upload-panel">
            <div className="kb-up-top">
              <span className="kb-up-title">📤 正在上传文档</span>
            </div>
            {uploadQueue.map(q => {
              const failed = q.status.startsWith('❌');
              const done = q.status.startsWith('✅');
              const secs = Math.max(0, Math.round((Date.now() - q.startedAt) / 1000));
              return (
                <div key={q.name} className="kb-upq-item">
                  <div className="kb-upq-head">
                    <span className="kb-upq-name" title={q.name}>{q.name}</span>
                    <span className={`kb-upq-state${failed ? ' kb-upq-fail' : done ? ' kb-upq-ok' : ''}`}>
                      {q.status}{!done && !failed && secs > 2 ? `（${secs}s）` : ''}
                    </span>
                  </div>
                  <Progress
                    percent={q.progress}
                    size="small"
                    showInfo={false}
                    status={failed ? 'exception' : done ? 'success' : 'active'}
                  />
                </div>
              );
            })}
          </div>
        )}

        {uploadJob && (
          <div className="kb-upload-panel">
            <div className="kb-up-top">
              <span className="kb-up-title">
                {uploadJob.done
                  ? (uploadJob.cancelled ? '⏹ 已中断上传' : '✅ 上传完成')
                  : (uploadJob.paused ? '⏸ 已暂停' : '📁 正在上传文件夹')}
              </span>
              <div className="kb-up-ctrls">
                {!uploadJob.done && (uploadJob.paused
                  ? <Button size="small" type="primary" ghost icon={<CaretRightOutlined />} onClick={resumeUpload}>继续</Button>
                  : <Button size="small" icon={<PauseOutlined />} onClick={pauseUpload}>暂停</Button>)}
                {!uploadJob.done && (
                  <Popconfirm overlayClassName="kb-pop-light" title="确认中断上传？已传完的保留，其余不再上传" okText="中断" cancelText="取消" onConfirm={cancelUpload}>
                    <Button size="small" danger icon={<StopOutlined />}>中断</Button>
                  </Popconfirm>
                )}
                {uploadJob.done && <Button size="small" onClick={() => setUploadJob(null)}>关闭</Button>}
              </div>
            </div>
            <div className="kb-up-stats">
              <span className="kb-up-chip">共 <b>{uploadJob.total}</b></span>
              <span className="kb-up-chip kb-up-pending">待传 <b>{Math.max(0, uploadJob.total - uploadJob.success - uploadJob.failed)}</b></span>
              <span className="kb-up-chip kb-up-ok">成功 <b>{uploadJob.success}</b></span>
              <span className="kb-up-chip kb-up-fail">失败 <b>{uploadJob.failed}</b></span>
            </div>
            <Progress
              percent={Math.min(100, Math.round(
                ((uploadJob.bytesDone + (uploadJob.current ? uploadJob.current.size * (uploadJob.current.progress || 0) / 100 : 0))
                  / Math.max(1, uploadJob.bytesTotal)) * 100
              ))}
              size="small"
              status={uploadJob.cancelled ? 'exception' : (uploadJob.done ? (uploadJob.failed > 0 ? 'exception' : 'success') : 'active')}
            />
            {uploadJob.current && (
              <div className="kb-up-current">
                <div className="kb-up-current-name" title={uploadJob.current.name}>
                  ⬆️ {uploadJob.current.name}
                  {uploadJob.current.size > 2 * 1024 * 1024 && (
                    <span style={{ marginLeft: 6, color: '#f59e0b', fontSize: 11 }}>
                      （{(uploadJob.current.size / 1024 / 1024).toFixed(1)}MB · 大文件,向量化耗时较长,请勿以为卡住）
                    </span>
                  )}
                </div>
                <Progress percent={uploadJob.current.progress} size="small" strokeColor="#22c55e" />
                {uploadJob.current.status && <div className="kb-up-current-step">{uploadJob.current.status}</div>}
              </div>
            )}
            {uploadJob.failedList.length > 0 && (
              <div className="kb-up-failed">
                <div className="kb-up-failed-title">❌ 失败 {uploadJob.failedList.length} 个（可重新上传这些文件）：</div>
                <div className="kb-up-failed-list">
                  {uploadJob.failedList.map((x, i) => (
                    <div key={i} className="kb-up-failed-item" title={x.error}>
                      <span className="kb-up-failed-name">{x.name}</span>
                      <span className="kb-up-failed-err">{x.error}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── 上传历史 ── */}
        {uploadHistory.length > 0 && (
          <div className="kb-recent-upload">
            <Text type="secondary" style={{ fontSize: 11, marginRight: 8 }}>📁 最近上传:</Text>
            {uploadHistory.map((h, i) => (
              <Tooltip key={i} title={`${h.time} · ${h.count}个文件`}>
                <Tag style={{ fontSize: 11, cursor: 'default', margin: '2px 4px' }}>{h.name}</Tag>
              </Tooltip>
            ))}
            <Button type="link" size="small" style={{ fontSize: 11, padding: 0 }} onClick={() => { setUploadHistory([]); localStorage.removeItem('kb_upload_history'); }}>清空</Button>
          </div>
        )}

        {/* ── Document List ── */}
        <Spin spinning={loading}>
          {docs.length === 0 && !loading ? (
            <div className="kb-empty">
              <InboxOutlined style={{ fontSize: 36, color: '#ccc' }} />
              <Text type="secondary">暂无文档，上传你的第一个文档开始构建知识库</Text>
            </div>
          ) : (
            <>
            <div className="kb-doc-list">
              <div className="kb-doc-header">
                <span style={{ flex: 1, paddingLeft: 8, fontSize: 11, color: '#999', fontWeight: 600 }}>文档名称</span>
                <span style={{ width: 70, fontSize: 11, color: '#999', fontWeight: 600 }}>类型</span>
                <span style={{ width: 60, fontSize: 11, color: '#999', fontWeight: 600, textAlign: 'center' }}>分块</span>
                <span style={{ width: 80, fontSize: 11, color: '#999', fontWeight: 600 }}>大小</span>
                <span style={{ width: 130, fontSize: 11, color: '#999', fontWeight: 600 }}>分类</span>
                <span style={{ width: 40 }} />
              </div>
              {docs.map(doc => (
                <div key={doc.name}
                  className={`kb-doc-row ${selectedDocs.has(doc.name) ? 'selected' : ''}`}
                  onClick={() => selectMode && toggleSelect(doc.name)}>
                  <span style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 8, overflow: 'hidden' }}>
                    {selectMode && (
                      <input type="checkbox" checked={selectedDocs.has(doc.name)} onChange={() => {}} style={{ flexShrink: 0 }} />
                    )}
                    <Text style={{ fontSize: 18, flexShrink: 0 }}>{TYPE_ICONS[doc.type] || '📎'}</Text>
                    <Tooltip title={doc.name}>
                      <Text strong ellipsis style={{ fontSize: 13, maxWidth: '100%' }}>{doc.name}</Text>
                    </Tooltip>
                  </span>
                  <Tag color={TYPE_COLORS[doc.type] || '#999'} style={{ width: 70, textAlign: 'center', fontSize: 11, margin: 0 }}>{doc.type}</Tag>
                  <Text type="secondary" style={{ width: 60, textAlign: 'center', fontSize: 12 }}>{doc.chunkCount}</Text>
                  <Text type="secondary" style={{ width: 80, fontSize: 12 }}>{formatSize(doc.size)}</Text>
                  <Select size="small" style={{ width: 130, fontSize: 11 }} value={doc.categoryId || 'default'}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(v) => handleSetCat(doc.name, v)}
                    options={[{ label: '默认', value: 'default' }, ...cats.map(c => ({ label: c.name, value: c.id }))]} />
                  <Popconfirm overlayClassName="kb-pop-light" title="确认删除？" onConfirm={() => handleDelete(doc.name)}>
                    <Button type="text" danger size="small"
                      loading={deletingDoc === doc.name}
                      icon={deletingDoc !== doc.name ? <DeleteOutlined style={{ fontSize: 13 }} /> : undefined}
                      onClick={(e) => e.stopPropagation()} />
                  </Popconfirm>
                </div>
              ))}
            </div>
            {/* ── 分页 ── */}
            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 12 }}>
                <Button size="small" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>上一页</Button>
                <Text type="secondary" style={{ fontSize: 12 }}>{page} / {totalPages}（共{docsTotal}个）</Text>
                <Button size="small" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>下一页</Button>
              </div>
            )}
            </>
          )}
        </Spin>
        </>)}
        </div>)}
        {/* ── 经验知识库 ── */}
        {kbTab === 'exp' && (<div key="exp">
          <Spin spinning={expLoading}>
            <div style={{ marginBottom: 12, display: 'flex', gap: 8 }}>
              <Input prefix={<SearchOutlined />} placeholder="搜索经验..." size="small" style={{ width: 240 }}
                allowClear value={expSearch} onChange={e => setExpSearch(e.target.value)}
                onPressEnter={handleExpSearch} />
              <Button size="small" icon={<SearchOutlined />} onClick={handleExpSearch}>搜索</Button>
              <Button size="small" icon={<ReloadOutlined />} onClick={() => { setExpSearch(''); loadExpKb(1); }}>
                刷新 ({expTotal})
              </Button>
            </div>
            {expEntries.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
                <InboxOutlined style={{ fontSize: 36 }} />
                <p>暂无经验记录</p>
                <p style={{ fontSize: 12 }}>使用团队协作或AI全流程设计模式后，问答将自动积累到这里</p>
              </div>
            ) : (
              <List size="small" dataSource={expEntries}
                pagination={{ current: expPage, pageSize: 20, total: expTotal, onChange: (p: number) => setExpPage(p), size: 'small', showTotal: (t: number) => `共 ${t} 条` }}
                renderItem={(item: ExpEntry) => (
                  <List.Item style={{ padding: '8px 12px', flexDirection: 'column', alignItems: 'stretch' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, width: '100%' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                          <Tag color={item.source === 'team_collab' ? 'blue' : 'green'} style={{ fontSize: 10 }}>{item.source === 'team_collab' ? '团队协作' : 'AI全流程'}</Tag>
                          <Tag color="purple" style={{ fontSize: 10 }}>{item.mode}</Tag>
                          {item.filePath && <Tag color="orange" style={{ fontSize: 10 }}>含文件</Tag>}
                          <Text type="secondary" style={{ fontSize: 11 }}><UserOutlined /> {item.userName || '未知'}</Text>
                          <div style={{ flex: 1 }} />
                          <Text type="secondary" style={{ fontSize: 11 }}>{item.createdAt ? new Date(item.createdAt).toLocaleDateString() : ''}</Text>
                        </div>
                        <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 4 }}>Q: {item.question}</Text>
                        <Text type="secondary" style={{ fontSize: 12, display: 'block', lineHeight: 1.4 }}>
                          A: {item.summary}
                          <Button type="link" size="small" style={{ padding: '0 4px', fontSize: 11 }} onClick={() => setExpDetail(item)}>查看详情</Button>
                        </Text>
                      </div>
                      <Popconfirm overlayClassName="kb-pop-light" title="确认删除此经验？" onConfirm={() => handleExpDelete(item.id)}>
                        <Button type="text" danger size="small" icon={<DeleteOutlined style={{ fontSize: 13 }} />} />
                      </Popconfirm>
                    </div>
                  </List.Item>
                )} />
            )}
          </Spin>
        </div>)}
        {/* ── 个人工作知识库 ── */}
        {kbTab === 'personal-work' && (<div key="personal-work">
          <Spin spinning={personalLoading}>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col xs={12} sm={6}><div className="kb-stat"><div className="kb-stat-val">{personalStats?.totalEntries ?? personalTotal}</div><div className="kb-stat-lbl"><UserOutlined /> 我的条目</div></div></Col>
              <Col xs={12} sm={6}><div className="kb-stat"><div className="kb-stat-val">{Object.keys(personalStats?.bySource || {}).length}</div><div className="kb-stat-lbl"><DatabaseOutlined /> 来源类型</div></div></Col>
            </Row>
            <div style={{ marginBottom: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
              <Tag color="blue">按当前用户强隔离</Tag>
              <Text type="secondary" style={{ fontSize: 12 }}>小灵只会匹配你的个人工作知识库，不会读取他人的个人记录。</Text>
              <div style={{ flex: 1 }} />
              <Button size="small" icon={<ReloadOutlined />} onClick={() => loadPersonalWorkKb(personalPage)}>刷新 ({personalTotal})</Button>
            </div>
            {personalEntries.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
                <InboxOutlined style={{ fontSize: 36 }} />
                <p>暂无个人工作记录</p>
                <p style={{ fontSize: 12 }}>与小灵/个人工作流程产生的记录会按你的用户ID单独沉淀到这里</p>
              </div>
            ) : (
              <List size="small" dataSource={personalEntries}
                pagination={{ current: personalPage, pageSize: 20, total: personalTotal, onChange: (p: number) => setPersonalPage(p), size: 'small', showTotal: (t: number) => `共 ${t} 条` }}
                renderItem={(item: WorkKbEntry) => {
                  const ws = parseWorkSource(item.source);
                  return (
                  <List.Item style={{ padding: '8px 12px', flexDirection: 'column', alignItems: 'stretch' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, width: '100%' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                          <Tag color="blue" style={{ fontSize: 10 }}>个人</Tag>
                          {ws.base && <Tag color="purple" style={{ fontSize: 10 }}>{ws.base}</Tag>}
                          <Text type="secondary" style={{ fontSize: 11 }}>chunk #{item.chunkIndex ?? 0}</Text>
                          <div style={{ flex: 1 }} />
                          {item.createdAt && <Text type="secondary" style={{ fontSize: 11 }}>发生 {new Date(item.createdAt).toLocaleString()}</Text>}
                          {ws.refinedAt && <Text type="secondary" style={{ fontSize: 11 }}>研判入库 {new Date(ws.refinedAt).toLocaleString()}</Text>}
                        </div>
                        <Text strong style={{ fontSize: 15, display: 'block', marginBottom: 4 }}>{item.title || '个人工作记录'}</Text>
                        <Text type="secondary" style={{ fontSize: 13, display: 'block', lineHeight: 1.6 }} ellipsis={{ rows: 2 } as any}>
                          {item.content || ''}
                        </Text>
                        <div style={{ marginTop: 6 }}>
                          <Button type="primary" ghost size="small" icon={<EyeOutlined />} onClick={() => setPersonalDetail(item)}>查看详情</Button>
                        </div>
                      </div>
                      <Popconfirm overlayClassName="kb-pop-light" title="确认删除此个人工作记录？" okText="删除" cancelText="取消" onConfirm={() => handlePersonalWorkDelete(item.id)}>
                        <Button type="text" danger size="small" icon={<DeleteOutlined style={{ fontSize: 14 }} />}>删除</Button>
                      </Popconfirm>
                    </div>
                  </List.Item>
                  );
                }} />
            )}
          </Spin>
        </div>)}
        {/* ── 团队工作知识库 ── */}
        {kbTab === 'team-work' && isAdmin && (<div key="team-work">
          <Spin spinning={teamLoading}>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col xs={12} sm={6}><div className="kb-stat"><div className="kb-stat-val">{teamStats?.totalEntries ?? teamTotal}</div><div className="kb-stat-lbl"><DatabaseOutlined /> 团队条目</div></div></Col>
              <Col xs={12} sm={6}><div className="kb-stat"><div className="kb-stat-val">{teamStats?.targetCount ?? Object.keys(teamStats?.byTarget || {}).length}</div><div className="kb-stat-lbl"><FolderOpenOutlined /> 群/目标</div></div></Col>
            </Row>
            <div style={{ marginBottom: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
              <Tag color="green">团队共享沉淀库</Tag>
              <Text type="secondary" style={{ fontSize: 12 }}>团队库未来检索不按用户隔离；这里只是 admin 管理入口。</Text>
              <div style={{ flex: 1 }} />
              <Button size="small" icon={<ReloadOutlined />} onClick={() => loadTeamWorkKb(teamPage)}>刷新 ({teamTotal})</Button>
            </div>
            {teamEntries.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
                <InboxOutlined style={{ fontSize: 36 }} />
                <p>暂无团队工作记录</p>
                <p style={{ fontSize: 12 }}>团队群聊/协作沉淀的工作记录会进入这个共享知识库</p>
              </div>
            ) : (
              <List size="small" dataSource={teamEntries}
                pagination={{ current: teamPage, pageSize: 20, total: teamTotal, onChange: (p: number) => setTeamPage(p), size: 'small', showTotal: (t: number) => `共 ${t} 条` }}
                renderItem={(item: WorkKbEntry) => (
                  <List.Item style={{ padding: '8px 12px', flexDirection: 'column', alignItems: 'stretch' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, width: '100%' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                          <Tag color="green" style={{ fontSize: 10 }}>团队</Tag>
                          <Tag color="cyan" style={{ fontSize: 10 }}>{item.targetName || item.targetId || '未知群'}</Tag>
                          <Text type="secondary" style={{ fontSize: 11 }}>chunk #{item.chunkIndex ?? 0}</Text>
                          <div style={{ flex: 1 }} />
                          <Text type="secondary" style={{ fontSize: 11 }}>{item.createdAt ? new Date(item.createdAt).toLocaleString() : ''}</Text>
                        </div>
                        <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 4 }}>{item.title || '团队工作记录'}</Text>
                        <Text type="secondary" style={{ fontSize: 12, display: 'block', lineHeight: 1.4 }} ellipsis>
                          {item.content || ''}
                          <Button type="link" size="small" style={{ padding: '0 4px', fontSize: 11 }} onClick={() => setTeamDetail(item)}>查看详情</Button>
                        </Text>
                      </div>
                      <Popconfirm overlayClassName="kb-pop-light" title="确认删除此团队工作记录？" onConfirm={() => handleTeamWorkDelete(item.id)}>
                        <Button type="text" danger size="small" icon={<DeleteOutlined style={{ fontSize: 13 }} />} />
                      </Popconfirm>
                    </div>
                  </List.Item>
                )} />
            )}
          </Spin>
        </div>)}
        {/* ── AI提炼 ── */}
        {kbTab === 'ai-refine' && (<div key="ai-refine">
          <AiRefinePanel />
        </div>)}
      </Card>

      {/* ── 经验详情弹窗 ── */}
      <Modal className="kb-modal-light" title="🧠 经验详情" open={!!expDetail} onCancel={() => setExpDetail(null)}
        footer={<Button onClick={() => setExpDetail(null)}>关闭</Button>} width={720}>
        {expDetail && (
          <div style={{ maxHeight: '60vh', overflow: 'auto' }}>
            <div style={{ marginBottom: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Tag color={expDetail.source === 'team_collab' ? 'blue' : 'green'}>{expDetail.source === 'team_collab' ? '团队协作' : 'AI全流程'}</Tag>
              <Tag color="purple">{expDetail.mode}</Tag>
              <Text type="secondary"><UserOutlined /> {expDetail.userName}</Text>
              <Text type="secondary">{expDetail.createdAt ? new Date(expDetail.createdAt).toLocaleString() : ''}</Text>
            </div>
            <Title level={5} style={{ marginTop: 0 }}>❓ 用户问题</Title>
            <div className="kb-exp-detail-block">
              {expDetail.question}
            </div>
            <Title level={5}>💡 AI回答</Title>
            <div className="kb-exp-detail-block kb-exp-answer">
              {expDetail.answer}
            </div>
          </div>
        )}
      </Modal>

      {/* ── 工作知识库详情弹窗（重新设计：深色卡片 + 亮白粗字，强制高对比） ── */}
      <Modal rootClassName="kb-work-detail-modal" title="📁 个人工作知识库详情" open={!!personalDetail} onCancel={() => setPersonalDetail(null)}
        footer={<Button type="primary" onClick={() => setPersonalDetail(null)}>关闭</Button>} width={760}>
        <style>{`
          .kb-work-detail-modal .ant-modal-content { background: #0f1e33 !important; border: 1px solid rgba(56,189,248,.28) !important; box-shadow: 0 24px 60px rgba(2,12,27,.55), 0 0 40px rgba(34,211,238,.12) !important; }
          .kb-work-detail-modal .ant-modal-header { background: transparent !important; border-bottom: 1px solid rgba(56,189,248,.20) !important; padding-bottom: 12px !important; }
          .kb-work-detail-modal .ant-modal-title { color: #f0f9ff !important; font-size: 18px !important; font-weight: 800 !important; letter-spacing: .5px; }
          .kb-work-detail-modal .ant-modal-body { color: #e2e8f0 !important; }
          .kb-work-detail-modal .ant-modal-close { color: #94a3b8 !important; }
          .kb-work-detail-modal .ant-modal-close:hover { color: #f0f9ff !important; }
          .kb-work-detail-modal .kb-wd-meta { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; margin-bottom: 16px; }
          .kb-work-detail-modal .kb-wd-time { color: #7dd3fc !important; font-size: 12px; }
          .kb-work-detail-modal .kb-wd-title { color: #ffffff !important; font-size: 17px !important; font-weight: 800 !important; margin: 0 0 14px 0 !important; line-height: 1.5; border-left: 4px solid #22d3ee; padding-left: 12px; }
          .kb-work-detail-modal .kb-wd-content { background: rgba(15,35,60,.6) !important; border: 1px solid rgba(56,189,248,.18) !important; border-radius: 14px; padding: 18px 20px; color: #f1f5f9 !important; font-size: 15px !important; line-height: 1.9 !important; white-space: pre-wrap; word-break: break-word; }
        `}</style>
        {personalDetail && (() => {
          const ws = parseWorkSource(personalDetail.source);
          return (
          <div style={{ maxHeight: '62vh', overflow: 'auto' }}>
            <div className="kb-wd-meta">
              <Tag color="blue">个人</Tag>
              {ws.base && <Tag color="purple">{ws.base}</Tag>}
              {personalDetail.createdAt && <span className="kb-wd-time">🕒 发生 {new Date(personalDetail.createdAt).toLocaleString()}</span>}
              {ws.refinedAt && <span className="kb-wd-time">🤖 研判入库 {new Date(ws.refinedAt).toLocaleString()}</span>}
            </div>
            <h3 className="kb-wd-title">{personalDetail.title || '个人工作记录'}</h3>
            <div className="kb-wd-content">{personalDetail.content || '暂无内容'}</div>
          </div>
          );
        })()}
      </Modal>
      <Modal rootClassName="kb-work-detail-modal" title="👥 团队工作知识库详情" open={!!teamDetail} onCancel={() => setTeamDetail(null)}
        footer={<Button type="primary" onClick={() => setTeamDetail(null)}>关闭</Button>} width={760}>
        {teamDetail && (
          <div style={{ maxHeight: '62vh', overflow: 'auto' }}>
            <div className="kb-wd-meta">
              <Tag color="green">团队</Tag>
              <Tag color="cyan">{teamDetail.targetName || teamDetail.targetId || '未知群'}</Tag>
              <span className="kb-wd-time">🕒 {teamDetail.createdAt ? new Date(teamDetail.createdAt).toLocaleString() : ''}</span>
            </div>
            <h3 className="kb-wd-title">{teamDetail.title || '团队工作记录'}</h3>
            <div className="kb-wd-content">{teamDetail.content || '暂无内容'}</div>
          </div>
        )}
      </Modal>
      {/* ── Category Modal ── */}
      <Modal className="kb-modal-light" title="新建分类" open={catModalOpen} onOk={handleCreateCat}
        onCancel={() => { setCatModalOpen(false); setCatName(''); setCatDesc(''); }}
        okText="创建" cancelText="取消" width={360}>
        <Input placeholder="分类名称" value={catName} onChange={e => setCatName(e.target.value)} style={{ marginBottom: 10 }} />
        <Input placeholder="描述（可选）" value={catDesc} onChange={e => setCatDesc(e.target.value)} style={{ marginBottom: 10 }} />
        <Space><Text type="secondary" style={{ fontSize: 12 }}>颜色:</Text>
          <Input type="color" value={catColor} onChange={e => setCatColor(e.target.value)} style={{ width: 36, height: 28, padding: 2 }} />
        </Space>
      </Modal>

      {/* ── LLM Dedup Confirm Modal ── */}
      <Modal className="kb-modal-light" title={dedupConfirmModal && dedupConfirmModal.skipped.length > 0 ? '🤖 AI 去重分析结果' : '🤖 AI 分析结果'}
        open={!!dedupConfirmModal}
        onOk={async () => {
          const m = dedupConfirmModal!;
          console.log(`[KB-CONFIRM] 确认上传按钮点击: files=${m.files.length}, allCount=${m.allCount}, skipped=${m.skipped.length}, folderName=${m.folderName}`);
          setDedupConfirmModal(null);
          if (m.skipped.length > 0) {
            setLlmStatus(`✅ 已跳过 ${m.skipped.length} 个旧版文件`);
          } else {
            setLlmStatus('✅ AI分析完成，开始上传');
          }
          if (m.folderName) saveUploadHistory(m.folderName, m.files.length);
          console.log(`[KB-CONFIRM] 调用executeFolderUpload...`);
          try {
            await executeFolderUpload(m.files, m.allCount, m.allCount - m.files.length);
            console.log(`[KB-CONFIRM] executeFolderUpload正常返回`);
          } catch (e: any) {
            console.error(`[KB-CONFIRM] executeFolderUpload异常: ${e.message}`);
          }
        }}
        onCancel={() => {
          setDedupConfirmModal(null);
          setLlmStatus('');
          if (folderInputRef.current) folderInputRef.current.value = '';
        }}
        okText="确认上传" cancelText="取消"
        width={640}>
        {/* ── 汇总统计（插入弹窗顶部，不依赖toast闪一下就没了）── */}
        {dedupConfirmModal && (() => {
          const m = dedupConfirmModal!;
          const parts: { label: string; count: number; detail?: string; color: string }[] = [];
          
          // 原始选中
          if (m.totalSelected > 0 && m.totalSelected !== m.allCount + m.duplicateCount) {
            parts.push({ label: '📁 文件夹共', count: m.totalSelected, color: '#666' });
          }
          // 类型过滤
          if (m.skippedTypes.length > 0) {
            const names = m.skippedTypes.slice(0,3).join('、') + (m.skippedTypes.length > 3 ? `等` : '');
            parts.push({ label: '❌ 类型不支持', count: m.skippedTypes.length, detail: `（${names}）`, color: '#ff4d4f' });
          }
          // txt>2MB 过滤
          if (m.skippedBigTxt.length > 0) {
            const names = m.skippedBigTxt.slice(0,3).join('、') + (m.skippedBigTxt.length > 3 ? `等` : '');
            parts.push({ label: '📄 txt超2MB跳过', count: m.skippedBigTxt.length, detail: `（${names}）`, color: '#fa8c16' });
          }
          // 大小过滤
          if (m.skippedOversize.length > 0) {
            const names = m.skippedOversize.slice(0,3).join('、') + (m.skippedOversize.length > 3 ? `等` : '');
            parts.push({ label: '📦 超过大小限制', count: m.skippedOversize.length, detail: `（${names}）`, color: '#fa8c16' });
          }
          // 同名重复
          if (m.duplicateCount > 0) {
            parts.push({ label: '📄 已有同名文件', count: m.duplicateCount, color: '#1677ff' });
          }
          // LLM去重
          if (m.llmRan && m.skipped.length > 0) {
            parts.push({ label: '🤖 AI检测旧版', count: m.skipped.length, color: '#722ed1' });
          }
          // 实际上传
          parts.push({ label: '✅ 将上传', count: m.files.length, color: '#52c41a' });

          if (parts.length <= 1 && m.totalSelected === m.files.length) return null; // 没有任何过滤，不显示汇总

          return (
            <div style={{
              background: 'linear-gradient(135deg, #f6f8fc, #eef1f6)',
              borderRadius: 8, padding: '12px 16px', marginBottom: 16,
              border: '1px solid #e0e4ea',
            }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#333' }}>
                📊 上传汇总
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px' }}>
                {parts.map((p, i) => (
                  <span key={i} style={{ fontSize: 12, color: p.color, maxWidth: '100%', wordBreak: 'break-all' }}>
                    <Text strong>{p.label}</Text> <Text strong style={{ fontSize: 13 }}>{p.count}</Text> 个
                    {p.detail && <Text type="secondary" style={{ fontSize: 11 }}>{p.detail}</Text>}
                    {i < parts.length - 1 && <Text type="secondary" style={{ margin: '0 4px' }}>→</Text>}
                  </span>
                ))}
              </div>
            </div>
          );
        })()}
        {/* 最终将上传的完整清单(不管有没有去重都列全,解决"保留数量对不上"——那是因为没重复的文件之前没列出来) */}
        {dedupConfirmModal && dedupConfirmModal.files.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ marginBottom: 6 }}>
              <Text strong style={{ color: '#52c41a' }}>✅ 最终将上传 {dedupConfirmModal.files.length} 个文件</Text>
              <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>（已排除重复/旧版后的完整清单）</Text>
            </div>
            <div style={{ maxHeight: 220, overflow: 'auto', border: '1px solid #f0f0f0', borderRadius: 6, padding: '8px 12px', background: '#fafafa' }}>
              {dedupConfirmModal.files.map((f, i) => (
                <div key={i} style={{ fontSize: 12, padding: '2px 0', color: '#666', wordBreak: 'break-all' }}>{i + 1}. {f.name}</div>
              ))}
            </div>
          </div>
        )}
        {dedupConfirmModal && dedupConfirmModal.skipped.length > 0 && (
          <div style={{ marginBottom: 6 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>以下是被判为重复/旧版、<Text type="secondary" strong style={{ fontSize: 12 }}>不会上传</Text>的文件分组：</Text>
          </div>
        )}
        {dedupConfirmModal && (!dedupConfirmModal.llmRan ? (
          /* LLM未运行 — 简单确认 */
          <div>
            <div style={{ marginBottom: 16 }}>
              <Text>
                {dedupConfirmModal.allCount === 1
                  ? '仅 1 个文件待上传。'
                  : `⚠️ AI 分析服务暂不可用，共 ${dedupConfirmModal.allCount} 个文件将直接上传。`}
              </Text>
            </div>
            <div style={{ maxHeight: 300, overflow: 'auto', border: '1px solid #f0f0f0', borderRadius: 6, padding: '12px 16px', background: '#fafafa' }}>
              {dedupConfirmModal.files.map((f, i) => (
                <div key={i} style={{ fontSize: 12, padding: '3px 0', color: '#666' }}>
                  {i + 1}. {f.name}
                </div>
              ))}
            </div>
            <div style={{ marginTop: 12 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>点击"确认上传"将继续上传以上全部文件。</Text>
            </div>
          </div>
        ) : dedupConfirmModal.skipped.length === 0 ? (
          /* LLM分析完成 — 无需去重 */
          <div>
            <div style={{ marginBottom: 16 }}>
              <Text>
                ✅ AI 分析完成，<Text strong style={{ color: '#52c41a' }}>{dedupConfirmModal.allCount}</Text> 个文件均为不同文档，无需去重。
              </Text>
            </div>
            <div style={{ maxHeight: 300, overflow: 'auto', border: '1px solid #f0f0f0', borderRadius: 6, padding: '12px 16px', background: '#fafafa' }}>
              {dedupConfirmModal.files.map((f, i) => (
                <div key={i} style={{ fontSize: 12, padding: '3px 0', color: '#666' }}>
                  {i + 1}. {f.name}
                </div>
              ))}
            </div>
            <div style={{ marginTop: 12 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>点击"确认上传"将继续上传以上全部文件。</Text>
            </div>
          </div>
        ) : (
          /* LLM分析完成 — 有去重 */
          <div>
            <div style={{ marginBottom: 16 }}>
              <Text>
                AI 分析完成，共 <Text strong>{dedupConfirmModal.allCount}</Text> 个文件，
                检测到 <Text strong style={{ color: '#ff4d4f' }}>{dedupConfirmModal.skipped.length}</Text> 个旧版/重复文件，
                将保留 <Text strong style={{ color: '#52c41a' }}>{dedupConfirmModal.allCount - dedupConfirmModal.skipped.length}</Text> 个最新版本。
              </Text>
            </div>
            <div style={{ maxHeight: 400, overflow: 'auto', border: '1px solid #f0f0f0', borderRadius: 6, padding: '12px 16px', background: '#fafafa' }}>
              {dedupConfirmModal.groups.map((g, i) => (
                <div key={i} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: i < dedupConfirmModal.groups.length - 1 ? '1px solid #e8e8e8' : 'none' }}>
                  <div style={{ marginBottom: 4 }}>
                    <Tag color="green" style={{ fontSize: 12 }}>✅ 保留</Tag>
                    <Text strong style={{ fontSize: 13 }}>{g.keep}</Text>
                    <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>（最新版本）</Text>
                  </div>
                  <div>
                    <Tag color="red" style={{ fontSize: 12 }}>❌ 忽略</Tag>
                    {g.skip.map((s, j) => (
                      <Text key={j} type="secondary" style={{ fontSize: 12, marginRight: 12 }}>{s}{j < g.skip.length - 1 ? '、' : ''}</Text>
                    ))}
                  </div>
                </div>
              ))}
              {/* 未被分组的跳过文件 */}
              {dedupConfirmModal.skipped.filter(s => !dedupConfirmModal.groups.some(g => g.skip.includes(s))).length > 0 && (
                <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #e8e8e8' }}>
                  <Tag color="orange" style={{ fontSize: 12, marginBottom: 4 }}>⚠️ 其他过滤</Tag>
                  {dedupConfirmModal.skipped.filter(s => !dedupConfirmModal.groups.some(g => g.skip.includes(s))).map((s, j) => (
                    <Text key={j} type="secondary" style={{ fontSize: 12, marginRight: 12 }}>{s}{j < dedupConfirmModal.skipped.filter(s2 => !dedupConfirmModal.groups.some(g => g.skip.includes(s2))).length - 1 ? '、' : ''}</Text>
                  ))}
                </div>
              )}
            </div>
            <div style={{ marginTop: 12 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>请确认以上分析结果，点击"确认上传"将继续上传保留的文件。</Text>
            </div>
          </div>
        ))}
      </Modal>

      {/* ── 批量重复文件弹窗 ── */}
      <Modal className="kb-modal-light" title={`📌 ${duplicateModal?.files.length || 0} 个文件已存在`}
        open={!!duplicateModal}
        onOk={() => {
          const m = duplicateModal!;
          setDuplicateModal(null);
          const action = dupAction;
          const allFiles = action === 'cover'
            ? [...m.files, ...m.newFiles]  // 覆盖：全部上传
            : m.newFiles;                    // 跳过：只传新文件
          if (m.folderName) saveUploadHistory(m.folderName, allFiles.length);
          proceedToLlmDedup(allFiles, allFiles.length, m.folderName, m.skippedTypes, m.skippedOversize, m.totalSelected, m.files.length, m.skippedBigTxt);
        }}
        onCancel={() => {
          setDuplicateModal(null);
          if (folderInputRef.current) folderInputRef.current.value = '';
          setLlmStatus('');
        }}
        okText="开始上传" cancelText="取消" width={640}
        footer={(_, { OkBtn, CancelBtn }) => (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Select value={dupAction} onChange={setDupAction} size="small" style={{ width: 140 }}>
              <Select.Option value="skip">全部跳过（仅传新文件）</Select.Option>
              <Select.Option value="cover">全部覆盖（重新上传）</Select.Option>
            </Select>
            <Space>
              <CancelBtn />
              <OkBtn />
            </Space>
          </div>
        )}>
        {/* ── 汇总统计 ── */}
        {duplicateModal && (() => {
          const m = duplicateModal!;
          const filtered = m.totalSelected - m.files.length - m.newFiles.length;
          if (filtered <= 0 && m.skippedTypes.length === 0 && m.skippedOversize.length === 0 && m.skippedBigTxt.length === 0) return null;
          const parts: string[] = [];
          if (m.totalSelected > 0) parts.push(`📁 文件夹共 ${m.totalSelected} 个文件`);
          if (m.skippedTypes.length > 0) {
            const names = m.skippedTypes.slice(0, 3).join('、') + (m.skippedTypes.length > 3 ? '等' : '');
            parts.push(`❌ 类型不支持 ${m.skippedTypes.length} 个（${names}）`);
          }
          if (m.skippedBigTxt.length > 0) {
            const names = m.skippedBigTxt.slice(0, 3).join('、') + (m.skippedBigTxt.length > 3 ? '等' : '');
            parts.push(`📄 txt超2MB跳过 ${m.skippedBigTxt.length} 个（${names}）`);
          }
          if (m.skippedOversize.length > 0) {
            const names = m.skippedOversize.slice(0, 3).join('、') + (m.skippedOversize.length > 3 ? '等' : '');
            parts.push(`📦 超过大小限制 ${m.skippedOversize.length} 个（${names}）`);
          }
          return (
            <div style={{ background: '#fff7e6', borderRadius: 6, padding: '10px 14px', marginBottom: 12, border: '1px solid #ffd591' }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {parts.join('  |  ')}
              </Text>
            </div>
          );
        })()}
        <div style={{ marginBottom: 12 }}>
          <Text>检测到 <Text strong style={{ color: '#fa8c16' }}>{duplicateModal?.files.length}</Text> 个文件已存在于知识库中：</Text>
        </div>
        <div style={{ maxHeight: 250, overflow: 'auto', border: '1px solid #f0f0f0', borderRadius: 6, padding: '8px 12px', background: '#fafafa' }}>
          {duplicateModal?.files.map((f, i) => (
            <div key={i} style={{ fontSize: 12, padding: '3px 0', color: '#666' }}>
              📄 {f.name}
            </div>
          ))}
        </div>
        <div style={{ marginTop: 12 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {duplicateModal && duplicateModal.newFiles.length > 0 ? (
              <>另有 <Text strong>{duplicateModal.newFiles.length}</Text> 个新文件。
              「覆盖」→ 全部 {duplicateModal.files.length + duplicateModal.newFiles.length} 个重新上传；
              「跳过」→ 仅传 {duplicateModal.newFiles.length} 个新文件。</>
            ) : duplicateModal ? (
              <>没有新文件。选「覆盖」将重传 {duplicateModal.files.length} 个已有文件；选「跳过」不上传任何文件。</>
            ) : ''}
          </Text>
        </div>
      </Modal>

      {/* ── 单个文件覆盖弹窗 ── */}
      <Modal className="kb-modal-light" title="文件已存在" open={!!dupModal}
        onOk={() => {
          const d = dupModal!;
          setDupModal(null);
          const setQ = (patch: Partial<{ progress: number; status: string }>) =>
            setUploadQueue(prev => prev.map(q => (q.name === d.file.name ? { ...q, ...patch } : q)));
          // 队列项在 handleUpload 里已创建（状态为"等待你确认"），这里直接接着更新即可
          setUploadQueue(prev => prev.some(q => q.name === d.file.name)
            ? prev
            : [...prev, { name: d.file.name, progress: 0, status: '📤 上传文件中…', startedAt: Date.now() }]);
          setQ({ progress: 5, status: '📤 上传文件中…（覆盖同名文件）' });
          kbApi.uploadDocumentStream(d.file, d.cat || undefined, (data) => setQ({ progress: data.progress, status: stepText(data) }))
            .then(() => {
              setQ({ progress: 100, status: '✅ 已入库' });
              noteUploaded(d.file.name);
              d.onSuccess?.({}, d.file);
              message.success(`上传成功: ${d.file.name}`);
              refreshDocs();
            })
            .catch((e: any) => {
              setQ({ progress: 100, status: `❌ 失败：${e.message}` });
              message.error(e.message);
            })
            .finally(() => {
              setTimeout(() => setUploadQueue(prev => prev.filter(q => q.name !== d.file.name)), 6000);
            });
        }}
        onCancel={() => {
          // 取消覆盖：把上传面板里那条"等待你确认"的记录清掉，否则会一直挂着
          const d = dupModal!;
          setUploadQueue(prev => prev.filter(q => q.name !== d.file.name));
          d.onSuccess?.({}, d.file);
          setDupModal(null);
        }}
        okText="覆盖" cancelText="跳过">
         <p>"{dupModal?.file?.name}" 已上传过，是否覆盖？</p>
      </Modal>

      {/* ── 权限管理 Modal（admin） ── */}
      <Modal className="kb-modal-light" title="🔐 知识库上传权限管理" open={permModalOpen} onCancel={() => setPermModalOpen(false)} footer={null} width={500}>
        <div style={{maxHeight:400,overflow:'auto'}}>
          {permUsers.map((u:any) => {
            const hasPerm = (u.userRoles || []).some((ur:any) => ur.role?.code === 'kb_upload') || u.roles?.includes('admin');
            return (
              <div key={u.id} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'8px 0',borderBottom:'1px solid #f5f5f5'}}>
                <div>
                  <Text strong>{u.name}</Text>
                  <Text type="secondary" style={{marginLeft:8,fontSize:12}}>{u.username}</Text>
                </div>
                <Button size="small" type={hasPerm?'default':'primary'} danger={hasPerm}
                  onClick={() => toggleKbPerm(u.id, hasPerm)}>
                  {hasPerm ? '移除上传权限' : '授予上传权限'}
                </Button>
              </div>
            );
          })}
        </div>
      </Modal>

      {/* ── 超大文件上传进度面板 ── */}
      <Modal
        className="kb-modal-light"
        title={<span><CloudServerOutlined style={{ marginRight: 8, color: '#d46b08' }} />超大文件上传</span>}
        open={largeUploadOpen}
        onCancel={closeLargePanel}
        footer={null}
        width={560}
        maskClosable={false}
        keyboard={false}
      >
        {!largeTask && (
          <Empty description="无任务" />
        )}
        {largeTask && (() => {
          const t = largeTask;
          const isRunning = ['uploading', 'queued', 'parsing', 'embedding', 'storing'].includes(t.status);
          const isDone = t.status === 'complete';
          const isFailed = t.status === 'failed' || t.status === 'cancelled';
          // 阶段标签
          const phaseLabel: Record<string, string> = {
            upload: '① 上传文件', parse: '② 解析内容', chunk: '③ 切片', embed: '④ 向量化', store: '⑤ 入库', done: '✓ 完成',
          };
          const statusColor = isDone ? '#52c41a' : isFailed ? '#ff4d4f' : '#1677ff';
          return (
            <div style={{ padding: '4px 0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <FileTextOutlined style={{ fontSize: 18, color: '#666' }} />
                <Text strong style={{ flex: 1, wordBreak: 'break-all' }}>{t.filename}</Text>
                <Tag>{formatSize(t.fileSize)}</Tag>
              </div>

              {/* 主进度条 */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <Text style={{ fontSize: 13 }}>{phaseLabel[t.phase] || t.phase}</Text>
                  <Text strong style={{ color: statusColor }}>
                    {t.status === 'uploading' ? `上传中 ${largeUploadByteProgress}%` : `${t.progress}%`}
                  </Text>
                </div>
                <Progress
                  percent={t.status === 'uploading' ? largeUploadByteProgress : t.progress}
                  status={isFailed ? 'exception' : isDone ? 'success' : 'active'}
                  strokeColor={statusColor}
                />
              </div>

              {/* 当前消息 */}
              <div style={{
                background: '#fafafa', border: '1px solid #f0f0f0',
                borderRadius: 6, padding: '10px 12px', marginBottom: 12,
                fontSize: 13, color: '#333',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {isRunning && <Spin size="small" />}
                  {isDone && <CheckCircleOutlined style={{ color: '#52c41a' }} />}
                  <span>{t.message}</span>
                </div>
              </div>

              {/* 详细子进度 */}
              {(t.totalPages || t.parseMode) && (
                <div style={{
                  background: '#f0f9ff', border: '1px solid #bae7ff',
                  borderRadius: 6, padding: '8px 12px', marginBottom: 12,
                  fontSize: 12, color: '#0050b3',
                }}>
                  {t.parseMode === 'ocr' && <div>🔍 解析模式：OCR（扫描件，单页串行约10-15秒/页）</div>}
                  {t.parseMode === 'text' && <div>📝 解析模式：文字层提取（快速）</div>}
                  {t.totalPages !== undefined && (
                    <div>📄 页数：{t.parsedPages || 0} / {t.totalPages}</div>
                  )}
                  {t.textLength !== undefined && t.textLength > 0 && (
                    <div>📊 已识别文本：{t.textLength.toLocaleString()} 字</div>
                  )}
                </div>
              )}

              {/* 错误详情 */}
              {isFailed && t.error && (
                <div style={{
                  background: '#fff1f0', border: '1px solid #ffccc7',
                  borderRadius: 6, padding: '8px 12px', marginBottom: 12,
                  fontSize: 12, color: '#a8071a', wordBreak: 'break-all',
                }}>
                  ❌ {t.error}
                </div>
              )}

              {/* 操作按钮 */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                {isRunning && (
                  <Popconfirm overlayClassName="kb-pop-light" title="确认取消上传？已识别的页面会丢失" onConfirm={handleLargeCancel}>
                    <Button size="small" danger>取消任务</Button>
                  </Popconfirm>
                )}
                {(isDone || isFailed) && (
                  <Button size="small" onClick={() => { setLargeTask(null); setLargeUploadOpen(false); }}>
                    关闭
                  </Button>
                )}
                <Button size="small" type="primary" onClick={closeLargePanel}>
                  最小化（任务后台继续）
                </Button>
              </div>

              {/* 提示 */}
              {isRunning && (
                <div style={{
                  marginTop: 12, fontSize: 11, color: '#999', textAlign: 'center',
                }}>
                  💡 任务在服务端独立运行，关闭此页或刷新浏览器都不会中断
                </div>
              )}
            </div>
          );
        })()}
      </Modal>
    </div>
  );
}
